import React from "react";
import { createRoot } from "react-dom/client";
import {
  canonicalize as cryptoCanonicalize,
  createPrfOnlyEncryptedKeyBundle,
  decryptEnvBundle,
  decodeBase64url,
  encryptEnvBundleForRecipients,
  generatePlainKeyset,
  parseDotenv,
  sha256Base64url as cryptoSha256Base64url,
  signManifest,
  stringifyDotenv,
  unlockKeyBundleWithPrf,
  upsertPrfWrapForKeyBundle,
  type EncryptedEnvBundle,
  type EncryptedKeyBundle,
  type PlainKeyset
} from "@envship/crypto";
import { Version } from "csp_evaluator/dist/csp";
import { CspEvaluator } from "csp_evaluator/dist/evaluator";
import { Severity } from "csp_evaluator/dist/finding";
import { CspParser } from "csp_evaluator/dist/parser";
import {
  clearUnlockSession,
  isUnlockComplete,
  loginUrlFor,
  sanitizeReturnTo,
  unlockUrlFor,
  writeUnlockSession
} from "./authRouting";
import {
  dashboardFlowStorageKey,
  defaultDashboardFlow,
  grantStatus as resolveGrantStatus,
  normalizeDashboardFlow,
  stepState as resolveStepState,
  type DashboardFlowState,
  type DashboardStep
} from "./dashboardFlow";
import { roleDescriptions, type WorkspaceRole } from "./inviteLogic";
import { docHref, resolveRoute } from "./routes";
import {
  classifySentinelResults,
  isAllowedPlatformScript,
  isLocalTrustedDevHost as isTrustedLocalHost,
  isSuspiciousStorageKey,
  isUnexpectedEnvShipStorageKey,
  type SentinelCheck,
  type SentinelScore,
  type SentinelStatus
} from "./sentinelLogic";
import "./styles.css";

const testLoginEmail = "mrlinvest@gmail.com";
const testLoginPassword = "MrLPassTest1!";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "https://api.envship.test";
const appNavigateEvent = "envship:navigate";
const paypalReceiverEmail = "payments@envship.com";

const browserFetch = window.fetch.bind(window);
window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
  const method = String(init.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
  const url = new URL(input instanceof Request ? input.url : String(input), window.location.href);
  if (["POST", "PUT", "DELETE"].includes(method) && url.origin === new URL(apiBaseUrl).origin) {
    const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set("X-EnvShip-CSRF", "1");
    return browserFetch(input, { ...init, headers });
  }
  return browserFetch(input, init);
};

type AccountVaultSession = {
  email: string;
  keyset: PlainKeyset;
  encryptedKeyBundle: EncryptedKeyBundle;
  credentialIdB64u: string;
};

let accountVaultSession: AccountVaultSession | null = null;

function setAccountVaultSession(session: AccountVaultSession | null) {
  accountVaultSession = session;
}

function getAccountVaultSession(email?: string | null) {
  if (!email) return accountVaultSession;
  return accountVaultSession?.email === email ? accountVaultSession : null;
}

function navigateWithinApp(path: string) {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new Event(appNavigateEvent));
}

function isLocalDevApp() {
  return ["envship.test", "localhost", "127.0.0.1"].includes(window.location.hostname);
}

function isStagingApp() {
  return window.location.hostname === "staging.envship.com";
}

function clearDashboardBrowserState() {
  setAccountVaultSession(null);
  window.sessionStorage.removeItem(dashboardFlowStorageKey);
  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of Object.keys(storage)) {
      if (key.startsWith("envship")) storage.removeItem(key);
    }
  }
}

function readDashboardFlow(): DashboardFlowState {
  try {
    const stored = window.sessionStorage.getItem(dashboardFlowStorageKey);
    if (!stored) return defaultDashboardFlow();
    const parsed = JSON.parse(stored) as Partial<DashboardFlowState> & { keyCreated?: boolean };
    return normalizeDashboardFlow(parsed);
  } catch {
    return defaultDashboardFlow();
  }
}

type GeneratedKeyFile = {
  filename: string;
  keyFile: {
    keyset_id: string;
    label: string;
    encryption_key_id: string;
    signing_key_id: string;
    public_metadata: { encryption_public_jwk: JsonWebKey; signing_public_jwk: JsonWebKey };
    created_at: string;
  };
  keyset: PlainKeyset;
  publicKeyId: string;
  signingKeyId: string;
};

type KeyfileBackup = {
  id: string;
  workspace_id: string;
  keyset_id: string;
  provider: "envship";
  encrypted_key_bundle: EncryptedKeyBundle;
  prf_credential_id_b64u?: string | null;
  updated_at: string;
};

type WebauthnCredentialSummary = {
  credential_id_b64u: string;
  prf_supported: boolean;
  prf_salt_b64u?: string | null;
  label?: string | null;
};

type InvitationSummary = {
  id: string;
  workspace_id: string;
  workspace_name?: string;
  email: string;
  role: WorkspaceRole;
  status: "pending" | "accepted" | "expired" | "revoked";
  invite_url?: string;
  expires_at: string;
  created_at: string;
};

type DashboardApiState = {
  workspaces: Array<{ id: string; name: string; role: WorkspaceRole; plan: string }>;
  active_workspace_id: string | null;
  projects: Array<{ id: string; workspace_id: string; name: string; slug: string; role?: string }>;
  bundles: Array<{ id: string; workspace_id: string; project_id: string; name: string }>;
  channels: Array<{ id: string; bundle_id: string; bundle_name: string; name: string; channel_ref: string; current_version_id?: string | null; current_version?: number | null }>;
  keysets: Array<{ keyset_id: string; label: string; encryption_key_id: string; signing_key_id: string; status: string; user_email?: string }>;
  members: Array<{ user_id: string; email: string; role: WorkspaceRole; status: string }>;
  grants: Array<{ id: string; channel_id: string; channel_ref: string; user_email?: string; keyset_id: string; capabilities: string[]; grant_status: string }>;
  versions: Array<{ id: string; channel_ref: string; version: number; is_current: boolean; created_at: string }>;
  machines: Array<{ id: string; name: string; signing_key_id: string; status: string; channels: string[] }>;
  deploy_tokens: Array<{ id: string; name: string; status: string; channels: string[]; created_at: string; last_used_at?: string | null; rotated_at?: string | null; revoked_at?: string | null }>;
  cli_workstations: Array<{ id: string; user_id: string; user_email?: string | null; workstation_id?: string | null; label?: string | null; source: string; status: string; created_at: string; last_used_at: string; revoked_at?: string | null }>;
  audit_events: Array<{ id: string; action: string; subject_type: string; created_at: string }>;
  suggestions: { bundle_name: string; channels: string[] };
};

type DeployChecklistItem = {
  id: string;
  label: string;
  status: "pending" | "running" | "passed" | "warning" | "failed";
  detail?: string;
};

type DeployOperation = {
  id: string;
  channel_ref?: string | null;
  version_id?: string | null;
  version?: number | null;
  operation_type: string;
  status: "saved" | "descriptor_written" | "cdn_refresh_pending" | "deploy_ready" | "revocation_pending_cdn_refresh" | "failed" | "failed_final";
  checklist: DeployChecklistItem[];
  warnings?: string[];
  descriptor_url?: string | null;
  purge_status?: string;
  purge_attempts?: number;
  purge_next_attempt_at?: string | null;
  purge_last_error?: string | null;
  notification_requested_at?: string | null;
  notification_sent_at?: string | null;
  created_at: string;
  updated_at: string;
  deploy_ready_at?: string | null;
};

type ProjectRole = "manager" | "developer" | "viewer";

type CliBootstrapSnippet = {
  code: string;
  command: string;
  expires_at: string;
  source: "first_run" | "invite_acceptance";
};

type SentinelImpact = "none" | "convenience" | "hardening" | "fail";

const sentinelCheckPlan: SentinelCheck[] = [
  { id: "secure-context", label: "Secure context", status: "queued", evidence: "Waiting to check HTTPS/browser context.", impact: "fail" },
  { id: "web-crypto", label: "Web Crypto", status: "queued", evidence: "Waiting to check local cryptography APIs.", impact: "fail" },
  { id: "script-surface", label: "Script surface", status: "queued", evidence: "Waiting to inspect page scripts.", impact: "fail" },
  { id: "csp", label: "Content Security Policy", status: "queued", evidence: "Waiting to read and evaluate same-origin CSP.", impact: "hardening" },
  { id: "app-shell-cache", label: "App-shell cache", status: "queued", evidence: "Waiting to inspect service worker cache names.", impact: "hardening" },
  { id: "storage-hygiene", label: "Storage hygiene", status: "queued", evidence: "Waiting to inspect browser storage key names.", impact: "fail" },
  { id: "network-surface", label: "Network surface", status: "queued", evidence: "Waiting to inspect configured frontend origins.", impact: "hardening" },
  { id: "frames-forms", label: "Iframe and form surface", status: "queued", evidence: "Waiting to inspect embedded and submit surfaces.", impact: "hardening" },
  { id: "extension-boundary", label: "Extension trust boundary", status: "queued", evidence: "Waiting to add local browser boundary note.", impact: "none" }
];

function isLocalTrustedDevHost() {
  return isTrustedLocalHost(window.location.hostname);
}

function sentinelStatusIcon(status: SentinelStatus) {
  if (status === "queued") return "•";
  if (status === "scanning") return "";
  if (status === "pass") return "✓";
  if (status === "warn") return "!";
  return "×";
}

function isApprovedFrameOrFormUrl(rawUrl: string) {
  try {
    const parsed = new URL(rawUrl, window.location.href);
    const allowedOrigins = new Set([
      window.location.origin,
      new URL(apiBaseUrl, window.location.href).origin,
      "https://api.envship.com",
      "https://api.envship.test",
      "https://envship.com",
      "https://envship.test"
    ]);
    return allowedOrigins.has(parsed.origin);
  } catch {
    return false;
  }
}

async function evaluateSentinelCheck(id: string): Promise<SentinelCheck> {
  switch (id) {
    case "secure-context": {
      return window.isSecureContext
        ? { id, label: "Secure context", status: "pass", evidence: "This page is running in a secure browser context.", impact: "fail" }
        : { id, label: "Secure context", status: "fail", evidence: "The page is not in a secure context, so browser vault handling should not continue.", impact: "fail" };
    }
    case "web-crypto": {
      return window.crypto?.subtle
        ? { id, label: "Web Crypto", status: "pass", evidence: "crypto.subtle is available for local browser vault operations.", impact: "fail" }
        : { id, label: "Web Crypto", status: "fail", evidence: "crypto.subtle is unavailable. EnvShip cannot safely run browser-local key operations here.", impact: "fail" };
    }
    case "script-surface": {
      const scripts = Array.from(document.scripts);
      const externalScripts = scripts.filter((script) => script.src);
      const unexpectedExternalScripts = externalScripts.filter((script) => !isAllowedPlatformScript(script.src, window.location.origin));
      const allowedPlatformScripts = externalScripts.filter((script) => {
        try {
          return new URL(script.src).origin !== window.location.origin && isAllowedPlatformScript(script.src, window.location.origin);
        } catch {
          return false;
        }
      });
      const inlineScripts = scripts.filter((script) => !script.src && (script.textContent ?? "").trim().length > 0);
      const inlineAllowedAsDevRuntime = inlineScripts.length > 0 && isLocalTrustedDevHost();
      if (unexpectedExternalScripts.length > 0) {
        return {
          id,
          label: "Script surface",
          status: "fail",
          evidence: `${unexpectedExternalScripts.length} unexpected external script tag${unexpectedExternalScripts.length === 1 ? "" : "s"} detected.`,
          details: "Sentinel allows EnvShip bundled scripts plus the Cloudflare Insights beacon only.",
          impact: "fail"
        };
      }
      return {
        id,
        label: "Script surface",
        status: inlineScripts.length > 0 && !inlineAllowedAsDevRuntime ? "warn" : "pass",
        evidence: `${externalScripts.length - allowedPlatformScripts.length} same-origin script tag${externalScripts.length - allowedPlatformScripts.length === 1 ? "" : "s"}; ${allowedPlatformScripts.length} allowed platform analytics script${allowedPlatformScripts.length === 1 ? "" : "s"}; ${inlineScripts.length} inline script block${inlineScripts.length === 1 ? "" : "s"}.`,
        details: inlineScripts.length > 0 && !inlineAllowedAsDevRuntime ? "Inline scripts can be normal in some dev shells, but production should keep script execution locked down with CSP." : undefined,
        impact: inlineScripts.length > 0 && !inlineAllowedAsDevRuntime ? "hardening" : "none"
      };
    }
    case "csp": {
      try {
        const response = await fetch(window.location.href, { cache: "no-store", credentials: "same-origin" });
        const header = response.headers.get("content-security-policy") ?? response.headers.get("Content-Security-Policy");
        if (!header) {
          return {
            id,
            label: "Content Security Policy",
            status: "warn",
            evidence: "No Content-Security-Policy header was visible to this local browser scan.",
            details: "Local dev servers often omit CSP. Production should serve a strict CSP so browser key handling has a smaller script surface.",
            impact: "hardening"
          };
        }
        const findings = new CspEvaluator(new CspParser(header).csp, Version.CSP3).evaluate().filter((finding) => {
          if (!isLocalTrustedDevHost()) return true;
          return ![
            "unsafe-inline",
            "localhost as source"
          ].some((allowedDevFinding) => finding.description.includes(allowedDevFinding));
        });
        const seriousFindings = findings.filter((finding) => finding.severity <= Severity.HIGH_MAYBE);
        const informationalFindings = findings.filter((finding) => finding.severity > Severity.HIGH_MAYBE && finding.severity <= Severity.INFO);
        if (seriousFindings.length > 0) {
          return {
            id,
            label: "Content Security Policy",
            status: "warn",
            evidence: `CSP analysis found ${seriousFindings.length} high-priority finding${seriousFindings.length === 1 ? "" : "s"}.`,
            details: seriousFindings.slice(0, 2).map((finding) => finding.description).join(" "),
            impact: "hardening"
          };
        }
        return {
          id,
          label: "Content Security Policy",
          status: "pass",
          evidence: informationalFindings.length > 0 ? `CSP is present; bundled evaluator logic found ${informationalFindings.length} informational note${informationalFindings.length === 1 ? "" : "s"} and no high-priority findings.` : "CSP is present and bundled evaluator logic found no high-priority findings.",
          impact: "none"
        };
      } catch {
        return {
          id,
          label: "Content Security Policy",
          status: "warn",
          evidence: "Sentinel could not read the same-origin document headers in this runtime.",
          details: "This does not touch EnvShip API data. It only means the browser did not expose the static page response for CSP analysis.",
          impact: "hardening"
        };
      }
    }
    case "app-shell-cache": {
      if (!("caches" in window)) {
        return { id, label: "App-shell cache", status: "pass", evidence: "Cache API is unavailable, so no app-shell cache names are present.", impact: "none" };
      }
      const cacheNames = await caches.keys();
      const suspicious = cacheNames.filter((name) => /(api|bundle|key|env|secret|plaintext|decrypted)/i.test(name) && !/static|shell|asset|vite/i.test(name));
      if (suspicious.length > 0) {
        return {
          id,
          label: "App-shell cache",
          status: "warn",
          evidence: `${suspicious.length} cache name${suspicious.length === 1 ? "" : "s"} look sensitive.`,
          details: suspicious.slice(0, 3).join(", "),
          impact: "hardening"
        };
      }
      return {
        id,
        label: "App-shell cache",
        status: "pass",
        evidence: cacheNames.length > 0 ? `${cacheNames.length} cache name${cacheNames.length === 1 ? "" : "s"} found; none look like API, bundle, key, or plaintext caches.` : "No browser caches are registered for this app shell.",
        impact: "none"
      };
    }
    case "storage-hygiene": {
      const storageKeys = [
        ...Array.from({ length: localStorage.length }, (_, index) => `local:${localStorage.key(index) ?? ""}`),
        ...Array.from({ length: sessionStorage.length }, (_, index) => `session:${sessionStorage.key(index) ?? ""}`)
      ].filter(Boolean);
      const suspiciousKeys = storageKeys.filter((storageKey) => isSuspiciousStorageKey(storageKey));
      const unexpectedEnvShipKeys = storageKeys.filter((storageKey) => isUnexpectedEnvShipStorageKey(storageKey));
      if (suspiciousKeys.length > 0) {
        return {
          id,
          label: "Storage hygiene",
          status: "fail",
          evidence: `Suspicious browser storage key${suspiciousKeys.length === 1 ? "" : "s"} detected.`,
          details: suspiciousKeys.slice(0, 3).join(", "),
          impact: "fail"
        };
      }
      if (unexpectedEnvShipKeys.length > 0) {
        return {
          id,
          label: "Storage hygiene",
          status: "warn",
          evidence: `${unexpectedEnvShipKeys.length} unexpected EnvShip storage key${unexpectedEnvShipKeys.length === 1 ? "" : "s"} found.`,
          details: unexpectedEnvShipKeys.slice(0, 3).join(", "),
          impact: "hardening"
        };
      }
      const persistenceEnabled = storageKeys.some((storageKey) => storageKey.endsWith("envship-dashboard-editor-encrypted-keyfile-v1"));
      return {
        id,
        label: "Storage hygiene",
        status: persistenceEnabled ? "warn" : "pass",
        evidence: persistenceEnabled ? "Only the encrypted browser vault shell is persisted." : "Only approved non-sensitive EnvShip browser storage keys are present.",
        details: persistenceEnabled ? "Convenient on trusted devices, but lower assurance than memory-only key handling." : undefined,
        impact: persistenceEnabled ? "convenience" : "none"
      };
    }
    case "network-surface": {
      try {
        const origin = new URL(apiBaseUrl, window.location.href).origin;
        const approved = new Set(["https://api.envship.com", "https://api.envship.test", "https://api-staging.envship.com", window.location.origin]);
        return approved.has(origin)
          ? { id, label: "Network surface", status: "pass", evidence: `Frontend API origin is ${origin}.`, impact: "none" }
          : { id, label: "Network surface", status: "warn", evidence: `Frontend API origin is ${origin}, which is not in Sentinel's expected EnvShip allowlist.`, impact: "hardening" };
      } catch {
        return { id, label: "Network surface", status: "warn", evidence: "Configured API base URL could not be parsed.", impact: "hardening" };
      }
    }
    case "frames-forms": {
      const frames = Array.from(document.querySelectorAll("iframe")).map((element) => element.getAttribute("src") ?? "");
      const forms = Array.from(document.querySelectorAll("form")).map((element) => element.getAttribute("action") ?? window.location.href);
      const unexpected = [...frames, ...forms].filter((url) => url && !isApprovedFrameOrFormUrl(url));
      if (unexpected.length > 0) {
        return {
          id,
          label: "Iframe and form surface",
          status: "warn",
          evidence: `${unexpected.length} iframe/form URL${unexpected.length === 1 ? "" : "s"} are outside the expected EnvShip identity surfaces.`,
          details: unexpected.slice(0, 2).join(", "),
          impact: "hardening"
        };
      }
      return {
        id,
        label: "Iframe and form surface",
        status: "pass",
        evidence: `${frames.length} iframe${frames.length === 1 ? "" : "s"} and ${forms.length} form${forms.length === 1 ? "" : "s"} checked.`,
        impact: "none"
      };
    }
    default:
      return {
        id,
        label: "Extension trust boundary",
        status: "warn",
        evidence: "Browser extensions can inspect pages depending on permissions.",
        details: "Ad blockers, password managers, and browser security extensions are normal. Sentinel cannot enumerate every extension, so this note does not lower the score.",
        impact: "none"
      };
  }
}

function SentinelPanel() {
  const [checks, setChecks] = React.useState<SentinelCheck[]>(sentinelCheckPlan);
  const [progress, setProgress] = React.useState(0);
  const [scanState, setScanState] = React.useState<"idle" | "running" | "complete">("idle");
  const [scoreState, setScoreState] = React.useState<{ score: SentinelScore | null; summary: string }>({
    score: null,
    summary: "Sentinel is checking the local browser surface before vault use."
  });

  const runScan = React.useCallback(() => {
    let cancelled = false;
    const scan = async () => {
      setScanState("running");
      setProgress(0);
      setScoreState({ score: null, summary: "Sentinel is checking the local browser surface before vault use." });
      setChecks(sentinelCheckPlan);
      const results: SentinelCheck[] = [];
      for (let index = 0; index < sentinelCheckPlan.length; index += 1) {
        const current = sentinelCheckPlan[index];
        setChecks((previous) => previous.map((check) => check.id === current.id ? { ...check, status: "scanning", evidence: "Checking now..." } : check));
        await new Promise((resolve) => setTimeout(resolve, 220));
        const result = await evaluateSentinelCheck(current.id);
        results.push(result);
        if (cancelled) return;
        setChecks((previous) => previous.map((check) => check.id === current.id ? result : check));
        setProgress(Math.round(((index + 1) / sentinelCheckPlan.length) * 100));
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
      if (cancelled) return;
      setScanState("complete");
      setProgress(100);
      setScoreState(classifySentinelResults(results));
    };
    void scan();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => runScan(), [runScan]);

  return (
    <section className="sentinel-panel" aria-live="polite">
      <div className="sentinel-heading">
        <div>
          <p className="eyebrow">EnvShip Sentinel</p>
          <h2>Local browser scanner</h2>
        </div>
        <button className="button ghost compact" type="button" onClick={() => { runScan(); }}>Rescan</button>
      </div>
      <div className="sentinel-score">
        <div className={`sentinel-score-badge ${scanState === "running" ? "scanning" : scoreState.score === "Fail" ? "fail" : scoreState.score === "B" ? "warn" : "pass"}`}>
          {scanState === "running" || !scoreState.score ? <span className="sentinel-spinner" aria-label="Scanning" /> : scoreState.score}
        </div>
        <div>
          <strong>{scanState === "complete" ? "Scan complete" : "Scanning Dashboard"}</strong>
          <p>{scoreState.summary}</p>
        </div>
      </div>
      <div className="sentinel-progress" aria-label={`Sentinel scan progress ${progress}%`}>
        <span style={{ width: `${progress}%` }} />
      </div>
      <ul className="sentinel-list">
        {checks.map((check) => (
          <li className={`sentinel-row ${check.status}`} key={check.id}>
            <span className="sentinel-row-icon">{check.status === "scanning" ? <span className="sentinel-mini-spinner" /> : sentinelStatusIcon(check.status)}</span>
            <div>
              <strong>{check.label}</strong>
              <p>{check.evidence}</p>
              {(check.status === "warn" || check.status === "fail") && check.details && <small>{check.details}</small>}
            </div>
          </li>
        ))}
      </ul>
      <p className="sentinel-footnote">
        CSP analysis is powered by bundled evaluator logic. Sentinel cannot certify a browser or enumerate every extension; the CLI remains the highest-assurance path for production changes.
      </p>
    </section>
  );
}

function base64url(bytes: Uint8Array) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function sha256Base64url(value: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return base64url(new Uint8Array(digest));
}

function canonicalize(value: unknown): string {
  return JSON.stringify(sortJson(value));
}

function sortJson(value: unknown): unknown {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map(sortJson);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, sortJson(item)])
    );
  }
  return null;
}

async function createEncryptedEmptyBundle(channelRef: string, recipients: Array<{ encryption_key_id: string; encryption_public_jwk: JsonWebKey }>) {
  const dataKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const rawDataKey = await crypto.subtle.exportKey("raw", dataKey);
  const wrappedRecipients = await Promise.all(recipients.map(async (recipient) => {
    const publicKey = await crypto.subtle.importKey("jwk", recipient.encryption_public_jwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
    const wrappedKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, rawDataKey);
    return { encryption_key_id: recipient.encryption_key_id, wrapped_key_b64u: base64url(new Uint8Array(wrappedKey)) };
  }));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify({ type: "envship.env_map.v1", env: {} }));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, dataKey, plaintext);
  return {
    type: "envship.encrypted_env_bundle.v1",
    algorithm: { content: "AES-GCM-256", recipient_wrap: "RSA-OAEP-SHA-256" },
    recipients: wrappedRecipients,
    cipher: { iv_b64u: base64url(iv), ciphertext_b64u: base64url(new Uint8Array(ciphertext)) },
    aad: { channel_ref: channelRef, created_at: new Date().toISOString() }
  };
}

function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function arrayBufferToBase64url(buffer: ArrayBuffer) {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return Promise.resolve(btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, ""));
}

function base64urlToArrayBuffer(value: string) {
  const bytes = decodeBase64url(value);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

function webauthnPublicKeyFromServer(publicKey: Record<string, unknown>) {
  const converted: Record<string, unknown> = {
    ...publicKey,
    challenge: base64urlToArrayBuffer(String(publicKey.challenge ?? "")),
    user: publicKey.user && typeof publicKey.user === "object"
      ? { ...(publicKey.user as PublicKeyCredentialUserEntity), id: base64urlToArrayBuffer(String((publicKey.user as { id?: string }).id ?? "")) }
      : undefined,
    excludeCredentials: Array.isArray(publicKey.excludeCredentials)
      ? publicKey.excludeCredentials.map((credential) => ({ ...(credential as PublicKeyCredentialDescriptor), id: base64urlToArrayBuffer(String((credential as { id?: string }).id ?? "")) }))
      : undefined,
    allowCredentials: Array.isArray(publicKey.allowCredentials)
      ? publicKey.allowCredentials.map((credential) => ({ ...(credential as PublicKeyCredentialDescriptor), id: base64urlToArrayBuffer(String((credential as { id?: string }).id ?? "")) }))
      : undefined
  };
  const prfSalt = (((publicKey.extensions as { prf?: { eval?: { first?: string } } } | undefined)?.prf?.eval?.first));
  if (prfSalt) converted.extensions = { prf: { eval: { first: base64urlToArrayBuffer(prfSalt) } } };
  return converted;
}

async function registerPasskeyForVault(apiUrl: string, label: string) {
  if (!("credentials" in navigator) || !window.PublicKeyCredential) throw new Error("Passkeys are not supported in this browser.");
  const optionsResponse = await fetch(`${apiUrl}/v1/webauthn/register/options`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label })
  });
  const options = await optionsResponse.json().catch(() => ({})) as { publicKey?: Record<string, unknown>; prf_salt_b64u?: string };
  if (!optionsResponse.ok || !options.publicKey) throw new Error("Passkey registration could not start.");
  const credential = await navigator.credentials.create({ publicKey: webauthnPublicKeyFromServer(options.publicKey) as unknown as PublicKeyCredentialCreationOptions }) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey registration was cancelled.");
  const response = credential.response as AuthenticatorAttestationResponse & { getTransports?: () => string[]; getPublicKey?: () => ArrayBuffer | null };
  const extensionResults = credential.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } };
  const credentialIdB64u = await arrayBufferToBase64url(credential.rawId);
  const publicKey = response.getPublicKey?.() ?? null;
  const verifyResponse = await fetch(`${apiUrl}/v1/webauthn/register/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      credential_id_b64u: credentialIdB64u,
      client_data_json_b64u: await arrayBufferToBase64url(response.clientDataJSON),
      public_key_spki_b64u: publicKey ? await arrayBufferToBase64url(publicKey) : undefined,
      transports: response.getTransports?.() ?? [],
      prf_supported: Boolean(extensionResults.prf?.results?.first),
      prf_salt_b64u: options.prf_salt_b64u,
      label
    })
  });
  const verify = await verifyResponse.json().catch(() => ({})) as { error?: string };
  if (!verifyResponse.ok) throw new Error(verify.error ?? "Passkey registration could not be verified.");
  return { credentialIdB64u, prfOutput: extensionResults.prf?.results?.first ? new Uint8Array(extensionResults.prf.results.first) : null };
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolvePromise, reject) => {
    const timeout = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then((value) => {
      window.clearTimeout(timeout);
      resolvePromise(value);
    }, (error) => {
      window.clearTimeout(timeout);
      reject(error);
    });
  });
}

async function authenticatePasskeyForVault(apiUrl: string) {
  if (!("credentials" in navigator) || !window.PublicKeyCredential) throw new Error("Passkeys are not supported in this browser.");
  const optionsResponse = await fetch(`${apiUrl}/v1/webauthn/authenticate/options`, { method: "POST", credentials: "include" });
  const options = await optionsResponse.json().catch(() => ({})) as { publicKey?: Record<string, unknown>; error?: string };
  if (!optionsResponse.ok || !options.publicKey) throw new Error(options.error ?? "Passkey unlock could not start.");
  const credential = await navigator.credentials.get({ publicKey: webauthnPublicKeyFromServer(options.publicKey) as unknown as PublicKeyCredentialRequestOptions }) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey unlock was cancelled.");
  const response = credential.response as AuthenticatorAssertionResponse;
  const extensionResults = credential.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } };
  const credentialIdB64u = await arrayBufferToBase64url(credential.rawId);
  const verifyResponse = await fetch(`${apiUrl}/v1/webauthn/authenticate/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      credential_id_b64u: credentialIdB64u,
      client_data_json_b64u: await arrayBufferToBase64url(response.clientDataJSON),
      authenticator_data_b64u: await arrayBufferToBase64url(response.authenticatorData),
      signature_b64u: await arrayBufferToBase64url(response.signature)
    })
  });
  const verify = await verifyResponse.json().catch(() => ({})) as { error?: string };
  if (!verifyResponse.ok) throw new Error(verify.error ?? "Passkey unlock could not be verified.");
  const prfOutput = extensionResults.prf?.results?.first;
  if (!prfOutput) throw new Error("This passkey did not return PRF unlock material. EnvShip Dashboard requires PRF-capable Authorized Devices.");
  return { credentialIdB64u, prfOutput: new Uint8Array(prfOutput) };
}

async function encryptKeysetForDeviceTransfer(keyset: PlainKeyset, publicJwk: JsonWebKey) {
  const transferKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "wrapKey"]);
  const publicKey = await crypto.subtle.importKey("jwk", publicJwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["wrapKey"]);
  const wrappedKey = await crypto.subtle.wrapKey("raw", transferKey, publicKey, { name: "RSA-OAEP" });
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, transferKey, new TextEncoder().encode(JSON.stringify(keyset)));
  return {
    type: "envship.authorized_device_transfer.v1",
    algorithm: { key_wrap: "RSA-OAEP-SHA-256", content: "AES-GCM-256" },
    wrapped_key_b64u: base64url(new Uint8Array(wrappedKey)),
    iv_b64u: base64url(iv),
    ciphertext_b64u: base64url(new Uint8Array(ciphertext))
  };
}

async function decryptKeysetFromDeviceTransfer(transfer: { wrapped_key_b64u?: string; iv_b64u?: string; ciphertext_b64u?: string }, privateKey: CryptoKey) {
  if (!transfer.wrapped_key_b64u || !transfer.iv_b64u || !transfer.ciphertext_b64u) throw new Error("Approved device transfer is incomplete.");
  const transferKey = await crypto.subtle.unwrapKey(
    "raw",
    base64urlToArrayBuffer(transfer.wrapped_key_b64u),
    privateKey,
    { name: "RSA-OAEP" },
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64urlToArrayBuffer(transfer.iv_b64u) },
    transferKey,
    base64urlToArrayBuffer(transfer.ciphertext_b64u)
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as PlainKeyset;
}

function App() {
  const [pathname, setPathname] = React.useState(window.location.pathname);
  React.useEffect(() => {
    const syncPath = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", syncPath);
    window.addEventListener(appNavigateEvent, syncPath);
    return () => {
      window.removeEventListener("popstate", syncPath);
      window.removeEventListener(appNavigateEvent, syncPath);
    };
  }, []);
  const page = resolveRoute(pathname);

  return (
    <>
      {page !== "dashboard" && page !== "authorizeCode" && page !== "unlock" && <Header />}
      {page === "home" && <HomePage />}
      {page === "pricing" && <PricingPage />}
      {page === "docs" && <DocsPage />}
      {page === "terms" && <TermsPage />}
      {page === "privacy" && <PrivacyPage />}
      {page === "dashboard" && <DashboardPage />}
      {page === "login" && <LoginPage />}
      {page === "unlock" && <UnlockPage />}
      {page === "invite" && <InvitePage />}
      {page === "authorizeCode" && <AuthorizeCodePage />}
      {page === "notFound" && <NotFoundPage />}
    </>
  );
}

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/">
        <span className="brand-mark" />
        EnvShip
      </a>
      <nav>
        <a href="/">Home</a>
        <a href="/pricing">Pricing</a>
        <a href="/docs">Docs</a>
        <a href="/privacy">Privacy</a>
        <a href="/dashboard">Dashboard</a>
      </nav>
      <a className="button small" href="/login">Sign up / Sign in</a>
    </header>
  );
}

function HomePage() {
  return (
    <main>
      <section className="hero split">
        <div>
          <p className="eyebrow">Cloudflare-only encrypted env delivery</p>
          <h1>ZeroTrust OAuth Passkey Encrypted, Reliable env Deploys Made Simple</h1>
          <p className="lead">
            You have the keys, we do not and can never access your secure data. Changes happen in-browser and
            stay encrypted through modern passkey PRF / passkey-derived unlock. EnvShip runs on a Cloudflare-only
            architecture for uptime reliability, with encrypted runtime pulls designed for Cloudflare CDN/R2 instead
            of Worker-heavy hot paths. The CLI lets teams publish, pull, and edit across CI, Docker, VPS, Laravel,
            Node, or any setup.
          </p>
          <div className="actions">
            <a className="button primary" href="/login">Start Free</a>
            <a className="button" href="/docs">Read setup guide</a>
          </div>
        </div>
        <CommandPanel
          title="First run"
          lines={[
            "pnpm dlx @envshipcom/cli auth login",
            "envship setup --workspace Acme --project Web --bundle web --channels staging,production",
            "envship channel publish web/production --in .env",
            "envship channel run web/production -- npm start"
          ]}
        />
      </section>

      <section className="section">
        <SectionIntro
          label="How it works"
          title="From signup to encrypted deploy in four steps."
          text="The product stays simple: OAuth gets you in, Authorized Devices unlock browser work, and machines only get the project channels they need."
        />
        <div className="timeline">
          {[
            ["Sign in", "Use OAuth with Google, GitHub, or Jira. Login proves account identity only."],
            ["Create workspace", "Pick your use case, create a workspace, and start on Free unless you choose a paid plan."],
            ["Create project", "Add the first project, bundle, channels, and Authorized Device so browser edits can unlock locally."],
            ["Deploy anywhere", "Publish, pull, edit, and run encrypted env bundles from Dashboard or the EnvShip CLI."]
          ].map(([title, text], index) => (
            <article className="timeline-item" key={title}>
              <span>{index + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section muted">
        <SectionIntro
          label="Where it runs"
          title="Channels for the deploy paths teams already use."
          text="Use stable bundle/channel names in scripts and docs. Versions change behind the channel; deploy commands stay the same, and production pulls are designed to read cached encrypted objects from cdn.envship.com."
        />
        <div className="card-grid three">
          {[
            ["Laravel / VPS", "laravel/production", "Run config cache, queues, and deploy scripts with decrypted env vars injected at runtime.", "envship channel run laravel/production -- php artisan queue:work"],
            ["Node apps", "web/staging", "Keep frontend, API, and worker deploy lanes separate without juggling raw `.env` files.", "envship channel run web/staging -- npm start"],
            ["Docker", "app/production", "Pull into a secret mount or inject into a container entrypoint without storing plaintext by default.", "envship channel pull app/production --out -"],
            ["GitHub Actions", "release/production", "Approve a machine identity once, then keep workflow files clean and scoped.", "envship channel run release/production -- npm run deploy"],
            ["GitLab CI", "api/production", "Use the same CLI commands and signed machine pulls in GitLab runners.", "envship channel pull api/production --out .env"]
          ].map(([title, channel, text, command]) => (
            <article className="card" key={title}>
              <code>{channel}</code>
              <h3>{title}</h3>
              <p>{text}</p>
              <code className="mini-command">{command}</code>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function PricingPage() {
  const [selectedPlan, setSelectedPlan] = React.useState("Free");
  const [checkoutMessage, setCheckoutMessage] = React.useState("");
  const plans = [
    ["Free", "$0", "1 workspace, 1 project, 1 bundle, 2 channels, browser Dashboard access included, 1 CLI Workstation per user, and 2 machine seats."],
    ["Pro", "$19/mo", "3 projects, 10 bundles, alpha/beta/staging/production, browser Dashboard access included, 2 CLI Workstations per user, 10 machine seats, and larger encrypted bundles."],
    ["Team", "$79/mo", "15 projects, 75 bundles, six channels, 5 CLI Workstations per user, 20 machines, and longer audit history."]
  ];
  const startSelectedPlan = async () => {
    setCheckoutMessage("");
    if (selectedPlan === "Free") {
      window.location.href = "/login?plan=free";
      return;
    }
    window.location.href = `/login?plan=${selectedPlan.toLowerCase()}`;
  };
  return (
    <main>
      <PageHero
        label="Pricing"
        title="Simple plans for encrypted deploy workflows."
        text="Start with one production path, then add seats, bundles, channels, machines, versions, and audit history as the team grows."
      />
      <section className="section">
        <div className="pricing-grid">
          {plans.map(([name, price, text]) => (
            <article className={`price-card ${selectedPlan === name ? "selected" : ""}`} key={name}>
              <h2>{name}</h2>
              <strong>{price}</strong>
              <p>{text}</p>
              <button type="button" className="button" onClick={() => setSelectedPlan(name)}>{selectedPlan === name ? `${name} selected` : `Choose ${name}`}</button>
            </article>
          ))}
        </div>
        <div className="pricing-cta">
          <button type="button" className="button primary" onClick={() => void startSelectedPlan()}>
            {selectedPlan === "Free" ? "Start Free" : "Start Pro"}
          </button>
          {checkoutMessage && <p className="muted">{checkoutMessage}</p>}
        </div>
      </section>
    </main>
  );
}

function NotFoundPage() {
  return (
    <main>
      <section className="hero compact-hero">
        <p className="eyebrow">404</p>
        <h1>That EnvShip page does not exist.</h1>
        <p className="lead">Use a known route so setup, docs, and dashboard actions stay predictable.</p>
        <div className="actions">
          <a className="button primary" href="/">Go home</a>
          <a className="button" href="/docs">Open docs</a>
          <a className="button" href="/dashboard">Open Dashboard</a>
        </div>
      </section>
    </main>
  );
}

function DocsPage() {
  const docs = [
    ["CLI setup", "Install, login, initialize, publish, pull, run, and edit."],
    ["First admin Authorized Device", "Create the encrypted browser vault shell and prepare for account-level passkey PRF unlock."],
    ["Access grants and encrypted access updates", "Understand why admins grant public keys, but authorized devices publish decryptable versions."],
    ["Workspace roles", "Separate owners, admins, developers, and viewers without handing everyone production access."],
    ["Project assignments", "Assign people to app-level work while keeping channel grants separate."],
    ["Channel grants", "Lock env access to exact lanes like `web/staging` and `api/production`."],
    ["Machine identities", "Authorize CI and VPS devices with browser-approved codes."],
    ["Public encrypted pull CDN", "Serve current encrypted channel bundles from Cloudflare CDN/R2 without Worker/D1 on runtime pulls."],
    ["Deploy status and CDN refresh", "See when an encrypted version is saved, when CDN refresh is pending, and when runtime pulls are ready."],
    ["Laravel deploys", "Use EnvShip with config cache, queues, Horizon, and VPS releases."],
    ["Docker secrets", "Inject env values into containers without long-lived plaintext files."],
    ["GitHub Actions", "Scoped machine pulls for production workflows."],
    ["Troubleshooting", "Signature, hash, recipient, and channel diagnostics."]
  ];

  return (
    <main>
      <PageHero
        label="Docs"
        title="Copy-paste guides for deploy teams."
        text="Start with CLI login, then pick the guide that matches where your app actually runs."
      />
      <section className="section">
        <div className="card-grid three">
          {docs.map(([title, text]) => (
            <article className="card" key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
              <a href={docHref(title)}>Open guide</a>
            </article>
          ))}
        </div>
      </section>
      {window.location.pathname === "/docs/access-grants" && <AccessGrantsGuide />}
      {window.location.pathname === "/docs/first-admin-authorized-device" && <FirstAdminAuthorizedDeviceGuide />}
      {window.location.pathname === "/docs/workspace-roles" && <AccessModelGuide kind="roles" />}
      {window.location.pathname === "/docs/project-assignments" && <AccessModelGuide kind="projects" />}
      {window.location.pathname === "/docs/channel-grants" && <AccessModelGuide kind="channels" />}
      {window.location.pathname === "/docs/public-pull-cdn" && <PublicPullCdnGuide />}
      {window.location.pathname === "/docs/deploy-status" && <DeployStatusGuide />}
    </main>
  );
}

function TermsPage() {
  return (
    <main>
      <PageHero
        label="Terms"
        title="EnvShip terms of service."
        text="These launch terms keep the product boundaries clear while EnvShip prepares for public availability."
      />
      <section className="section doc-detail">
        <h2>Service Use</h2>
        <p>EnvShip helps teams publish, pull, edit, and deploy encrypted environment bundles. You are responsible for the systems, applications, users, machines, deploy tokens, and data you connect to EnvShip.</p>
        <h2>Security Model</h2>
        <p>EnvShip stores encrypted vault bundles, encrypted env bundles, public metadata, audit metadata, and operational records. EnvShip is not designed to receive plaintext environment values, private vault keys, passkey PRF outputs, vault unlock keys, private machine keys, or provider tokens.</p>
        <h2>Accounts And Access</h2>
        <p>You are responsible for keeping OAuth accounts, Authorized Devices, deploy tokens, and machine credentials under your control. If all Authorized Devices are lost, EnvShip support cannot decrypt or recover customer secrets.</p>
        <h2>Availability</h2>
        <p>EnvShip is built on Cloudflare services. We work to keep deploy and pull paths reliable, but no hosted service can guarantee uninterrupted availability.</p>
        <h2>Contact</h2>
        <p>Questions about these terms can be sent to support@envship.com.</p>
      </section>
    </main>
  );
}

function PrivacyPage() {
  return (
    <main>
      <PageHero
        label="Privacy"
        title="EnvShip privacy policy."
        text="EnvShip is designed so your encrypted secrets stay unreadable to the service."
      />
      <section className="section doc-detail">
        <h2>Data We Process</h2>
        <p>EnvShip processes account identity data such as email address and provider id, workspace/project metadata, public key metadata, audit events, billing state, deploy status, and encrypted bundle objects.</p>
        <h2>Encrypted Customer Secrets</h2>
        <p>Environment values are encrypted before they are uploaded. EnvShip does not intentionally receive plaintext env values, private vault keys, passkey PRF outputs, vault unlock keys, or decrypted bundles.</p>
        <h2>OAuth Providers</h2>
        <p>OAuth is used to verify account identity. Provider access is limited to identity and email scopes where available. OAuth login alone does not decrypt EnvShip secrets.</p>
        <h2>Operational Data</h2>
        <p>EnvShip stores operational records such as deploy status, audit logs, machine identity metadata, public encrypted pull descriptors, and rate-limit counters to keep the service secure and reliable.</p>
        <h2>Contact</h2>
        <p>Privacy questions can be sent to support@envship.com.</p>
      </section>
    </main>
  );
}

function PublicPullCdnGuide() {
  return (
    <section className="section doc-detail">
      <p className="eyebrow">Cost and runtime pull model</p>
      <h2>Public URLs serve encrypted current bundles only.</h2>
      <p>
        EnvShip's high-frequency deploy/runtime path is designed to avoid Worker and D1 calls after setup. The
        control plane still authorizes publishes, grants, revokes, audits, and access updates, but machines can pull
        current encrypted descriptors and bundle objects from Cloudflare CDN backed by a public R2 bucket.
      </p>
      <ul>
        <li>Production private storage stays in `envshipprod`; production public encrypted pulls use `envshipprodpublic` on `cdn.envship.com`.</li>
        <li>Staging private storage stays in `envshipstaging`; staging public encrypted pulls use `envshipstagingpublic` on `cdnstaging.envship.com`.</li>
        <li>Stable current descriptors live at opaque HMAC-derived paths like `p/&lt;pull_id&gt;.json`.</li>
        <li>Immutable encrypted manifests and bundles live at versioned paths and can be cached long-term.</li>
        <li>Permission changes publish freshly wrapped current bundles and purge the stable descriptor URL from Cloudflare Cache.</li>
      </ul>
      <p>
        These URLs are public transport for ciphertext, not public secrets. The browser and CLI still verify hashes and
        signatures, then decrypt locally only when the user or machine has a valid authorized key.
      </p>
    </section>
  );
}

function DeployStatusGuide() {
  return (
    <section className="section doc-detail">
      <p className="eyebrow">Deploy reliability</p>
      <h2>Saved and runtime-ready are tracked separately.</h2>
      <p>
        EnvShip records a deploy operation whenever a browser publish, encrypted access update, rollback-as-current,
        revoke, or rotation affects the current encrypted runtime descriptor. The checklist shows validation,
        encryption/signing, private upload, current pointer update, public descriptor write, purge request, CDN refresh,
        and runtime readiness as separate steps.
      </p>
      <ul>
        <li>`deploy_ready` means the encrypted version is saved and the CDN descriptor refresh is confirmed.</li>
        <li>`cdn_refresh_pending` means EnvShip saved the current version, but machines may briefly see the previous cached descriptor.</li>
        <li>`revocation_pending_cdn_refresh` is a higher-severity warning for access removal, machine revocation, or token rotation effects.</li>
        <li>EnvShip retries purge-by-URL in a Scheduled Worker up to five times with backoff and records each attempt in D1.</li>
        <li>Runtime pulls always respect the CDN descriptor path so saved versions and deploy-ready status stay consistent.</li>
      </ul>
      <p>
        If a delayed state occurs, the Dashboard offers opt-in browser push notifications. EnvShip does not use email
        for deploy-ready notifications in the launch plan.
      </p>
    </section>
  );
}

function AccessModelGuide(props: { kind: "roles" | "projects" | "channels" }) {
  const content = {
    roles: {
      label: "Workspace roles",
      title: "Workspace roles protect billing, admin, and security decisions.",
      text: "A workspace is the top-level EnvShip boundary. Owners control billing and final authority, admins manage day-to-day security, developers work inside assigned projects, and viewers see allowed metadata without decrypt or publish power.",
      items: [
        "owner: billing, deletion, role management, all grants",
        "admin: projects, members, grants, machines, channels, audit",
        "developer: assigned project work plus explicit channel grants",
        "viewer: metadata and audit visibility only"
      ]
    },
    projects: {
      label: "Project assignments",
      title: "Project assignment gives app visibility, not automatic env access.",
      text: "Assigning someone to a project lets them see the app context and work inside that project. It does not give them decrypt access. Env access is always granted at the bundle/channel layer.",
      items: [
        "manager: manage bundles, channels, and grants inside the project",
        "developer: work on assigned bundles/channels after grants are approved",
        "viewer: inspect project metadata and audit history",
        "project access without channel grant means metadata only"
      ]
    },
    channels: {
      label: "Channel grants",
      title: "Channel grants are the exact env access boundary.",
      text: "A channel is a stable deploy lane such as `web/staging` or `api/production`. Grant staging without production, grant pull without publish, and grant encrypted access updates only to trusted key holders.",
      items: [
        "view_metadata: see the channel exists and inspect safe metadata",
        "decrypt/edit/publish: work with encrypted env values locally",
        "update encrypted access/rollback: repair recipient access or move pointers",
        "manage_grants/manage_machines: administer people and deploy identities"
      ]
    }
  }[props.kind];

  return (
    <section className="section guide">
      <p className="eyebrow">{content.label}</p>
      <h2>{content.title}</h2>
      <p>{content.text}</p>
      <ul className="guide-list">
        {content.items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </section>
  );
}

function FirstAdminAuthorizedDeviceGuide() {
  return (
    <section className="section guide">
      <p className="eyebrow">First admin setup</p>
      <h2>Your first EnvShip vault is the root of workspace editing.</h2>
      <p>
        The first admin flow is intentionally explicit because it defines the safety boundary for the whole
        workspace. Your EnvShip login gives you access to metadata: workspace membership, projects, bundles,
        channels, audit history, and billing. The Authorized Device model gives the browser cryptographic power:
        decrypting env values, signing manifests, and publishing encrypted versions. EnvShip stores only the
        encrypted vault backup, public key metadata, and encrypted bundle data.
      </p>
      <div className="callout strong">
        <strong>Authorized Devices are required.</strong> EnvShip uses account-level passkey PRF unlock and
        does not provide portable password-style recovery for Dashboard launch.
      </div>
      <h3>First admin checklist</h3>
      <ol className="guide-list">
        <li>Sign in to EnvShip Dashboard with your normal account.</li>
        <li>Choose or create the workspace that will own your projects, bundles, channels, machines, and audit events.</li>
        <li>Create the encrypted EnvShip vault in Dashboard with passkey PRF unlock.</li>
        <li>Choose a label that identifies the Authorized Device, such as `founder-laptop` or `admin-macbook`.</li>
        <li>Add the first Authorized Device before Dashboard editing is available.</li>
        <li>Add a second Authorized Device before production-ready admin work.</li>
        <li>Register only public key metadata with the workspace.</li>
        <li>Create the first project, bundle, and channel, such as `web/production`.</li>
        <li>Publish the first encrypted version from the CLI or Dashboard.</li>
      </ol>
      <CommandPanel
        title="Automation equivalent"
        lines={[
          "envship auth login",
          "envship setup --workspace Acme --project Web --bundle web --channels production",
          "envship machine install --name web-production --channel web/production",
          "envship machine deploy-token-create --project <project-id> --channel web/production"
        ]}
      />
      <h3>What the admin can and cannot do</h3>
      <p>
        An admin can invite users, approve public keys, revoke keys, create channels, review audit events, and
        decide who should be able to publish. An admin cannot use the EnvShip server to decrypt old ciphertext
        or silently add a new recipient to an existing encrypted version. If a newly granted user needs access to
        the current version, an authorized key holder must update encrypted access and publish a new encrypted version.
      </p>
    </section>
  );
}

function AccessGrantsGuide() {
  return (
    <section className="section guide">
      <p className="eyebrow">ZeroTrust access model</p>
      <h2>Admins grant access. Authorized devices make current envs decryptable.</h2>
      <p>
        EnvShip separates account permission from cryptographic access. This is the piece that makes the
        system safe even though the product can feel simple. A workspace admin can decide that a teammate,
        laptop, CI runner, or VPS should be allowed to access a bundle/channel. That admin action stores public
        metadata: who owns the key, which public encryption key is allowed, which signing key can publish, and
        which bundle/channel the grant applies to. It does not give EnvShip the ability to decrypt anything.
      </p>
      <p>
        Existing encrypted bundle versions were created for a specific recipient set. Each version contains
        encrypted env data plus recipient envelopes. Those envelopes wrap the bundle data key to each recipient
        public key. If Alice is added tomorrow, old versions do not magically contain an envelope for Alice.
        EnvShip cannot add one because EnvShip does not know the plaintext env values or the bundle data key.
      </p>
      <div className="callout">
        <strong>The rule:</strong> granting a public key allows future access decisions; publishing or updating encrypted access
        creates a new encrypted version that the new key can actually decrypt.
      </div>
      <StatusList />
      <h3>The safe teammate flow</h3>
      <ol className="guide-list">
        <li>Alice signs in and adds an Authorized Device.</li>
        <li>Alice registers only public key metadata with the workspace.</li>
        <li>An admin grants Alice access to `web/staging`, `web/production`, or another channel.</li>
        <li>EnvShip records the grant and shows that current versions need an encrypted access update before Alice can decrypt them.</li>
        <li>Bob, who already has access, updates encrypted access from Dashboard or CLI.</li>
        <li>Bob's device decrypts the current bundle locally, never on EnvShip servers.</li>
        <li>Bob's device encrypts the same env map to the updated active recipient list.</li>
        <li>Bob signs the new manifest and publishes a new immutable version.</li>
        <li>The channel pointer moves to the new version.</li>
        <li>Alice can now pull/decrypt that new version with her own local private key.</li>
      </ol>
      <CommandPanel
        title="Grant then update access"
        lines={[
          "envship grant create web/production --keyset kst_alice --preset editor",
          "envship channel rewrap web/production",
          "envship channel pull web/production --out .env"
        ]}
      />
      <h3>Why this remains ZeroTrust</h3>
      <p>
        The server stores public keys, grants, manifests, object pointers, billing state, and audit history. It
        does not store private keys, plaintext env values, or the unwrapped data key. If the server
        is asked to add a recipient to old ciphertext, it cannot. The only safe path is for an authorized client
        to create a new encrypted version.
      </p>
      <h3>What revocation means</h3>
      <p>
        Revoking a key stops it from being included in future versions and blocks normal authorized pulls. It
        does not erase the fact that older ciphertext may already be decryptable by a private key that previously
        had access. For high-risk revocations, publish a new version excluding the key and rotate the actual
        application secrets that may have existed in old versions.
      </p>
    </section>
  );
}

function StatusList() {
  const statuses = [
    ["No key registered", "The user is signed in, but has not registered public key metadata for this workspace."],
    ["Pending grant", "A public key exists, but no admin has approved it for the selected bundle/channel."],
    ["Granted for future versions", "The key is approved and will be included the next time an authorized client publishes or updates encrypted access."],
    ["Needs encrypted access update", "The current version was encrypted before the key was granted. A current recipient must publish a new encrypted version."],
    ["Included in current version", "The current encrypted version contains an envelope for this key, so the authorized local vault can decrypt it."],
    ["Revoked", "The key is inactive and must not be included in future versions or normal pull flows."]
  ];

  return (
    <div className="status-grid">
      {statuses.map(([title, text]) => (
        <article key={title}>
          <h4>{title}</h4>
          <p>{text}</p>
        </article>
      ))}
    </div>
  );
}

function rateLimitMessage(body: { error?: string; retry_after_seconds?: number }, fallback: string) {
  if (body.error === "rate_limited") {
    return `Too many requests. Try again in ${body.retry_after_seconds ?? 60} seconds.`;
  }
  return body.error ?? fallback;
}

function usePendingActions() {
  const [pendingActions, setPendingActions] = React.useState<Record<string, boolean>>({});
  const pendingRef = React.useRef<Record<string, boolean>>({});
  const runAction = React.useCallback(async <T,>(key: string, action: () => Promise<T>) => {
    if (pendingRef.current[key]) return undefined;
    pendingRef.current = { ...pendingRef.current, [key]: true };
    setPendingActions(pendingRef.current);
    try {
      return await action();
    } finally {
      const { [key]: _done, ...rest } = pendingRef.current;
      pendingRef.current = rest;
      setPendingActions(rest);
    }
  }, []);
  const isPending = React.useCallback((key: string) => Boolean(pendingRef.current[key]), []);
  return { pendingActions, runAction, isPending };
}

function deployStatusLabel(status: DeployOperation["status"]) {
  const labels: Record<DeployOperation["status"], string> = {
    saved: "Saved",
    descriptor_written: "Descriptor written",
    cdn_refresh_pending: "CDN refresh pending",
    deploy_ready: "Deploy ready",
    revocation_pending_cdn_refresh: "Revocation pending CDN refresh",
    failed: "Failed",
    failed_final: "Failed after retries"
  };
  return labels[status] ?? status;
}

function deployChecklistSymbol(status: DeployChecklistItem["status"]) {
  if (status === "passed") return "OK";
  if (status === "warning") return "!";
  if (status === "failed") return "X";
  if (status === "running") return "...";
  return "-";
}

function deployNeedsNotification(deploy: DeployOperation | null) {
  return Boolean(deploy && ["cdn_refresh_pending", "revocation_pending_cdn_refresh", "failed"].includes(deploy.status) && !deploy.notification_requested_at);
}

function DashboardPage() {
  const params = new URLSearchParams(window.location.search);
  const dashboardSection = window.location.pathname.split("/")[2] ?? "overview";
  const [auth, setAuth] = React.useState<{ loading: boolean; email: string | null; error: string | null }>({
    loading: true,
    email: null,
    error: null
  });
  const [bootstrap, setBootstrap] = React.useState<{
    workspaceId: string;
    workspaceName: string;
    workspaceRole: WorkspaceRole;
    projectId: string;
    projectName: string;
    bundleName: string;
    channels: string[];
  }>({
    workspaceId: "",
    workspaceName: "My Workspace",
    workspaceRole: "owner",
    projectId: "",
    projectName: "My First Project",
    bundleName: "web",
    channels: ["staging", "production"]
  });
  const [setupDraft, setSetupDraft] = React.useState({
    workspaceName: "My Workspace",
    projectName: "My First Project",
    bundleName: "web",
    channels: "staging, production"
  });
  const [flow, setFlow] = React.useState<DashboardFlowState>(() => readDashboardFlow());
  const [dashboardState, setDashboardState] = React.useState<DashboardApiState | null>(null);
  const [keyActionMessage, setKeyActionMessage] = React.useState("");
  const [lastGeneratedKeyFile, setLastGeneratedKeyFile] = React.useState<GeneratedKeyFile | null>(null);
  const [keyfileBackups, setKeyfileBackups] = React.useState<KeyfileBackup[]>([]);
  const [vaultCredentials, setVaultCredentials] = React.useState<WebauthnCredentialSummary[]>([]);
  const [authorizedDevices, setAuthorizedDevices] = React.useState<Array<WebauthnCredentialSummary & { status?: string; created_at?: string; last_used_at?: string | null }>>([]);
  const [accountVaultProductionReady, setAccountVaultProductionReady] = React.useState(false);
  const [riskAcknowledgeReady, setRiskAcknowledgeReady] = React.useState(false);
  const [riskAcknowledgeChecked, setRiskAcknowledgeChecked] = React.useState(false);
  const [inviteForm, setInviteForm] = React.useState<{ email: string; role: WorkspaceRole; sendEmail: boolean }>({
    email: "",
    role: "developer",
    sendEmail: false
  });
  const [inviteMessage, setInviteMessage] = React.useState("");
  const [inviteError, setInviteError] = React.useState("");
  const [inviteResult, setInviteResult] = React.useState<InvitationSummary | null>(null);
  const [pendingInvites, setPendingInvites] = React.useState<InvitationSummary[]>([]);
  const [isCreatingInvite, setIsCreatingInvite] = React.useState(false);
  const [machineName, setMachineName] = React.useState("github-actions-production");
  const [machineMessage, setMachineMessage] = React.useState("");
  const [deployTokenName, setDeployTokenName] = React.useState("Autoscaling deploy token");
  const [deployTokenResult, setDeployTokenResult] = React.useState<{ token: string; channels: string[] } | null>(null);
  const [deployTokens, setDeployTokens] = React.useState<DashboardApiState["deploy_tokens"]>([]);
  const [adminMessage, setAdminMessage] = React.useState("");
  const [channelDraft, setChannelDraft] = React.useState("beta");
  const [grantDraft, setGrantDraft] = React.useState<{ keysetId: string; channelId: string; preset: string }>({
    keysetId: "",
    channelId: "",
    preset: "editor"
  });
  const [memberRoleDrafts, setMemberRoleDrafts] = React.useState<Record<string, WorkspaceRole>>({});
  const [machineInstructions, setMachineInstructions] = React.useState("");
  const [unlockedKeyset, setUnlockedKeyset] = React.useState<PlainKeyset | null>(() => getAccountVaultSession()?.keyset ?? null);
  const [editorChannelId, setEditorChannelId] = React.useState("");
  const [editorText, setEditorText] = React.useState("");
  const [editorExpectedVersionId, setEditorExpectedVersionId] = React.useState<string | null>(null);
  const [editorBundleHash, setEditorBundleHash] = React.useState("");
  const [editorMessage, setEditorMessage] = React.useState("");
  const [isEditorBusy, setIsEditorBusy] = React.useState(false);
  const [activeDeploy, setActiveDeploy] = React.useState<DeployOperation | null>(null);
  const [deployHistory, setDeployHistory] = React.useState<DeployOperation[]>([]);
  const [deployStatusMessage, setDeployStatusMessage] = React.useState("");
  const [cliBootstrapSnippet, setCliBootstrapSnippet] = React.useState<{
    command: string;
    code: string;
    expires_at: string;
    source: "first_run" | "invite_acceptance";
  } | null>(null);
  const [cliBootstrapMessage, setCliBootstrapMessage] = React.useState("");
  const [projectAssignmentDrafts, setProjectAssignmentDrafts] = React.useState<Record<string, ProjectRole>>({});
  const [grantPresetDrafts, setGrantPresetDrafts] = React.useState<Record<string, string>>({});
  const [auditFilter, setAuditFilter] = React.useState("");
  const { pendingActions, runAction, isPending } = usePendingActions();
  const refreshInFlight = React.useRef<Promise<DashboardApiState | null> | null>(null);
  const signedIn = Boolean(auth.email);
  const hasActiveWorkspace = Boolean(dashboardState?.active_workspace_id && dashboardState.channels.length > 0);
  const firstUser = signedIn && (params.get("first_user") === "1" || !hasActiveWorkspace || (!dashboardState?.versions.length && !flow.versionPublished));
  const sessionEmail = auth.email ?? testLoginEmail;
  const parsedChannels = setupDraft.channels.split(",").map((channel) => channel.trim()).filter(Boolean);
  const visibleChannels = parsedChannels.length ? parsedChannels : bootstrap.channels;
  const selectedChannel = `${setupDraft.bundleName || bootstrap.bundleName}/${visibleChannels[0] ?? "staging"}`;
  const selectedChannelRecord = dashboardState?.channels.find((channel) => channel.channel_ref === selectedChannel) ?? dashboardState?.channels[0] ?? null;
  const editorChannel = dashboardState?.channels.find((channel) => channel.id === (editorChannelId || selectedChannelRecord?.id)) ?? selectedChannelRecord;
  const grantStatus = resolveGrantStatus(flow, hasActiveWorkspace);
  const inviteAccepted = params.get("invite_accepted") === "1";

  const saveFlow = (next: DashboardFlowState) => {
    window.sessionStorage.setItem(dashboardFlowStorageKey, JSON.stringify(next));
    setFlow(next);
  };

  const updateFlow = (next: Partial<DashboardFlowState>) => {
    saveFlow({ ...flow, ...next, updatedAt: new Date().toISOString() });
  };

  const refreshDashboardState = React.useCallback(async (options: { force?: boolean } = {}) => {
    if (refreshInFlight.current && !options.force) return refreshInFlight.current;
    const request = (async () => {
      const response = await fetch(`${apiBaseUrl}/v1/dashboard/state`, { credentials: "include" });
      if (!response.ok) return null;
      const state = await response.json() as DashboardApiState;
      setDashboardState(state);
      const workspace = state.workspaces[0];
      const project = state.projects[0];
      const bundle = state.bundles[0];
      const channelOrder = ["staging", "production", "alpha", "beta", "gamma", "omega"];
      const channels = state.channels
        .filter((channel) => channel.bundle_id === bundle?.id)
        .sort((a, b) => {
          const aIndex = channelOrder.indexOf(a.name);
          const bIndex = channelOrder.indexOf(b.name);
          if (aIndex !== -1 || bIndex !== -1) return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
          return a.name.localeCompare(b.name);
        });
      if (workspace && project) {
        setBootstrap({
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          workspaceRole: workspace.role,
          projectId: project.id,
          projectName: project.name,
          bundleName: bundle?.name ?? state.suggestions.bundle_name,
          channels: channels.length ? channels.map((channel) => channel.name) : state.suggestions.channels
        });
        setSetupDraft({
          workspaceName: workspace.name,
          projectName: project.name,
          bundleName: bundle?.name ?? state.suggestions.bundle_name,
          channels: (channels.length ? channels.map((channel) => channel.name) : state.suggestions.channels).join(", ")
        });
      }
      return state;
    })();
    refreshInFlight.current = request;
    try {
      return await request;
    } finally {
      if (refreshInFlight.current === request) refreshInFlight.current = null;
    }
  }, []);

  const refreshKeyfileBackups = React.useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/v1/account-vault`, { credentials: "include" });
      if (!response.ok) return;
      const body = await response.json() as { vault?: { encrypted_key_bundle: EncryptedKeyBundle; keyset_id: string; updated_at: string } | null; devices?: Array<WebauthnCredentialSummary & { status?: string; created_at?: string; last_used_at?: string | null }>; production_ready?: boolean };
      setKeyfileBackups(body.vault ? [{
        id: "account-vault",
        workspace_id: bootstrap.workspaceId,
        keyset_id: body.vault.keyset_id,
        provider: "envship",
        encrypted_key_bundle: body.vault.encrypted_key_bundle,
        prf_credential_id_b64u: null,
        updated_at: body.vault.updated_at
      }] : []);
      setVaultCredentials(body.devices ?? []);
      setAuthorizedDevices(body.devices ?? []);
      setAccountVaultProductionReady(Boolean(body.production_ready));
    } catch {
      // Account vault metadata is convenience state; passkey unlock is still attempted on the unlock route.
    }
  }, [bootstrap.workspaceId]);

  const saveWorkspaceSetup = async () => {
    const nextChannels = visibleChannels.length ? visibleChannels : ["staging", "production"];
    const response = await fetch(`${apiBaseUrl}/v1/dashboard/setup`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        workspace_name: setupDraft.workspaceName || "My Workspace",
        project_name: setupDraft.projectName || "My First Project",
        bundle_name: setupDraft.bundleName || "web",
        channels: nextChannels
      })
    });
    if (!response.ok) {
      setKeyActionMessage("Workspace setup could not be saved.");
      return;
    }
    await refreshDashboardState();
    updateFlow({ workspaceConfigured: true, expandedStep: "keyfile" });
  };

  const editWorkspaceSetup = () => {
    const current = {
      workspaceName: bootstrap.workspaceName || setupDraft.workspaceName || "My Workspace",
      projectName: bootstrap.projectName || setupDraft.projectName || "My First Project",
      bundleName: bootstrap.bundleName || setupDraft.bundleName || "web",
      channels: (bootstrap.channels.length ? bootstrap.channels : visibleChannels).join(", ")
    };
    setSetupDraft(current);
    saveFlow({
      ...flow,
      workspaceConfigured: true,
      expandedStep: "workspace",
      updatedAt: new Date().toISOString()
    });
    navigateWithinApp("/dashboard?first_user=1");
  };

  const createAnotherWorkspace = () => {
    window.sessionStorage.removeItem(dashboardFlowStorageKey);
    setLastGeneratedKeyFile(null);
    setKeyActionMessage("");
    setSetupDraft({
      workspaceName: "New Workspace",
      projectName: "New Project",
      bundleName: "web",
      channels: "staging, production"
    });
    setFlow(defaultDashboardFlow());
    window.history.replaceState(null, "", "/dashboard?first_user=1");
  };

  const advanceFlow = async (step: "key" | "register" | "grant" | "publish" | "confirm-download" | "download-again") => {
    const now = new Date().toISOString();
    if (step === "key") {
      setKeyActionMessage("");
      try {
        const session = getAccountVaultSession(auth.email);
        if (!session) throw new Error("Unlock with an Authorized Device before continuing setup.");
        const keyset = session.keyset;
        const keyFile = {
          keyset_id: keyset.keyset_id,
          label: keyset.label,
          encryption_key_id: keyset.encryption_key_id,
          signing_key_id: keyset.signing_key_id,
          public_metadata: {
            encryption_public_jwk: keyset.encryption_public_jwk,
            signing_public_jwk: keyset.signing_public_jwk
          },
          created_at: keyset.created_at
        };
        const generated = {
          filename: `${flow.keyLabel}.account-vault.json`,
          keyFile,
          keyset,
          publicKeyId: keyset.encryption_key_id,
          signingKeyId: keyset.signing_key_id
        };
        setLastGeneratedKeyFile(generated);
        setUnlockedKeyset(keyset);
        saveFlow({
          ...flow,
          keyGenerated: true,
          keyDownloadConfirmed: true,
          publicRegistered: false,
          grantConfirmed: false,
          versionPublished: false,
          publicKeyId: keyset.encryption_key_id,
          signingKeyId: keyset.signing_key_id,
          expandedStep: "keyfile",
          updatedAt: now
        });
        setKeyActionMessage("Authorized Device account vault is ready. Add a second device before production-ready admin work.");
      } catch (error) {
        setKeyActionMessage(error instanceof Error ? error.message : "EnvShip vault creation failed.");
      }
      return;
    }
    if (step === "download-again" && lastGeneratedKeyFile) {
      downloadJson(lastGeneratedKeyFile.filename, lastGeneratedKeyFile.keyFile);
        setKeyActionMessage("Advanced encrypted vault JSON downloaded again. Confirm once it is saved somewhere safe.");
      return;
    }
    if (step === "confirm-download" && flow.keyGenerated) {
      saveFlow({ ...flow, keyDownloadConfirmed: true, expandedStep: "register", updatedAt: now });
      return;
    }
    if (step === "register" && flow.keyDownloadConfirmed) {
      const session = getAccountVaultSession(auth.email);
      if (!bootstrap.workspaceId || !session?.keyset?.keyset_id || !session.keyset.encryption_public_jwk || !session.keyset.signing_public_jwk) {
        setKeyActionMessage("Create or unlock the vault in this browser session before registering public metadata.");
        return;
      }
      const response = await fetch(`${apiBaseUrl}/v1/account-vault/register-public-metadata`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: bootstrap.workspaceId })
      });
      if (!response.ok) {
        setKeyActionMessage("Public key metadata could not be registered.");
        return;
      }
      await refreshDashboardState();
      saveFlow({ ...flow, publicRegistered: true, expandedStep: "grant", updatedAt: now });
      return;
    }
    if (step === "grant" && flow.publicRegistered) {
      const session = getAccountVaultSession(auth.email);
      if (!selectedChannelRecord || !session?.keyset) {
        setKeyActionMessage("Create a channel and registered public metadata before granting access.");
        return;
      }
      const response = await fetch(`${apiBaseUrl}/v1/channel-grants`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: selectedChannelRecord.id, keyset_id: session.keyset.keyset_id, preset: "editor" })
      });
      if (!response.ok) {
        setKeyActionMessage("Channel grant could not be saved.");
        return;
      }
      await refreshDashboardState();
      saveFlow({ ...flow, grantConfirmed: true, expandedStep: "publish", updatedAt: now });
      return;
    }
    if (step === "publish" && flow.grantConfirmed) {
      if (!selectedChannelRecord) {
        setKeyActionMessage("Create a channel before publishing.");
        return;
      }
      const localPublicJwk = getAccountVaultSession(auth.email)?.keyset.encryption_public_jwk;
      if (!localPublicJwk) {
        setKeyActionMessage("Create or unlock the vault in this browser session before publishing.");
        return;
      }
      const recipientResponse = await fetch(`${apiBaseUrl}/v1/channels/${selectedChannelRecord.id}/recipients`, {
        credentials: "include"
      });
      const recipientBody = await recipientResponse.json().catch(() => ({})) as {
        expected_current_version_id?: string | null;
        recipients?: Array<{ encryption_key_id: string; encryption_public_jwk: JsonWebKey }>;
      };
      const recipients = recipientBody.recipients?.length
        ? recipientBody.recipients
        : [{ encryption_key_id: flow.publicKeyId, encryption_public_jwk: localPublicJwk }];
      const encryptedBundle = await createEncryptedEmptyBundle(selectedChannel, recipients);
      const bundleHash = await sha256Base64url(canonicalize(encryptedBundle));
      const response = await fetch(`${apiBaseUrl}/v1/versions/publish`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel_id: selectedChannelRecord.id,
          expected_current_version_id: recipientBody.expected_current_version_id ?? selectedChannelRecord.current_version_id ?? null,
          publisher_signing_key_id: flow.signingKeyId,
          manifest: { channel: selectedChannel, created_by: "dashboard" },
          encrypted_bundle: encryptedBundle,
          bundle_sha256_b64u: bundleHash
        })
      });
      if (!response.ok) {
        setKeyActionMessage("First encrypted version could not be published.");
        return;
      }
      await refreshDashboardState();
      saveFlow({ ...flow, versionPublished: true, expandedStep: "publish", updatedAt: now });
    }
  };

  const resetFlow = async () => {
    clearDashboardBrowserState();
    setLastGeneratedKeyFile(null);
    setKeyActionMessage("");
    setUnlockedKeyset(null);
    setDashboardState(null);
    setFlow(defaultDashboardFlow());
    if (isLocalDevApp()) {
      const response = await fetch(`${apiBaseUrl}/v1/dev/reset-local-data`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      if (!response.ok) {
        setKeyActionMessage("Local data reset failed. Make sure the Worker dev server is running.");
        return;
      }
      window.location.href = "/login?return_to=%2Fdashboard%3Ffirst_user%3D1";
      return;
    }
    window.history.replaceState(null, "", "/dashboard?first_user=1");
  };

  const signOut = () => {
    window.sessionStorage.removeItem(dashboardFlowStorageKey);
    clearUnlockSession(window.sessionStorage);
    fetch(`${apiBaseUrl}/v1/logout`, { method: "POST", credentials: "include" }).finally(() => {
      window.location.href = "/login?return_to=/dashboard";
    });
  };

  React.useEffect(() => {
    let active = true;
    async function loadDashboard() {
      try {
        const meResponse = await fetch(`${apiBaseUrl}/v1/me`, { credentials: "include" });
        if (!meResponse.ok) {
          if (active) {
            setAuth({ loading: false, email: null, error: null });
            window.location.href = loginUrlFor(`${window.location.pathname}${window.location.search}`);
          }
          return;
        }
        const me = await meResponse.json() as { ok: boolean; user?: { email: string } };
        const email = me.user?.email ?? null;
        const vaultSession = getAccountVaultSession(email);
        if (email && (!isUnlockComplete(window.sessionStorage, email) || !vaultSession)) {
          window.location.href = unlockUrlFor(`${window.location.pathname}${window.location.search}`);
          return;
        }
        if (vaultSession) setUnlockedKeyset(vaultSession.keyset);
        if (active) {
          await refreshDashboardState();
          await refreshKeyfileBackups();
        }
        if (active) setAuth({ loading: false, email, error: null });
      } catch {
        if (active) setAuth({ loading: false, email: null, error: "Could not reach the local EnvShip API." });
      }
    }
    void loadDashboard();
    return () => {
      active = false;
    };
  }, [refreshDashboardState, refreshKeyfileBackups]);

  const loadInvitations = React.useCallback(async () => {
    if (!bootstrap.workspaceId || !flow.workspaceConfigured) return;
    try {
      const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/invitations`, {
        credentials: "include"
      });
      if (!response.ok) return;
      const body = await response.json() as { invitations?: InvitationSummary[] };
      setPendingInvites(body.invitations ?? []);
    } catch {
      setInviteError("Could not load pending invitations.");
    }
  }, [bootstrap.workspaceId, flow.workspaceConfigured]);

  const loadDeployTokens = React.useCallback(async () => {
    if (!bootstrap.projectId) {
      setDeployTokens([]);
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/projects/${bootstrap.projectId}/deploy-tokens`, {
      credentials: "include"
    });
    if (!response.ok) {
      setDeployTokens([]);
      return;
    }
    const body = await response.json().catch(() => ({})) as { deploy_tokens?: DashboardApiState["deploy_tokens"] };
    setDeployTokens(body.deploy_tokens ?? []);
  }, [bootstrap.projectId]);

  const loadDeployHistory = React.useCallback(async () => {
    if (!bootstrap.projectId) {
      setDeployHistory([]);
      return [];
    }
    const response = await fetch(`${apiBaseUrl}/v1/projects/${bootstrap.projectId}/deploys`, {
      credentials: "include"
    });
    if (!response.ok) {
      setDeployHistory([]);
      return [];
    }
    const body = await response.json().catch(() => ({})) as { deploys?: DeployOperation[] };
    const deploys = body.deploys ?? [];
    setDeployHistory(deploys);
    return deploys;
  }, [bootstrap.projectId]);

  const loadDeployDetail = React.useCallback(async (deployId: string) => {
    const response = await fetch(`${apiBaseUrl}/v1/deploys/${deployId}`, {
      credentials: "include"
    });
    if (!response.ok) return null;
    const body = await response.json().catch(() => ({})) as { deploy?: DeployOperation };
    if (body.deploy) {
      setActiveDeploy(body.deploy);
      setDeployHistory((current) => [body.deploy as DeployOperation, ...current.filter((deploy) => deploy.id !== body.deploy?.id)].slice(0, 25));
    }
    return body.deploy ?? null;
  }, []);

  const createCliBootstrapCode = React.useCallback(async (source: "first_run" | "invite_acceptance") => {
    setCliBootstrapMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/cli-bootstrap-codes`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        workspace_id: bootstrap.workspaceId || undefined,
        project_id: bootstrap.projectId || undefined,
        source
      })
    });
    const body = await response.json().catch(() => ({})) as {
      command?: string;
      code?: string;
      expires_at?: string;
      source?: "first_run" | "invite_acceptance";
      error?: string;
    };
    if (!response.ok || !body.command || !body.code || !body.expires_at) {
      setCliBootstrapMessage(body.error === "authorized_device_required"
        ? "Connect the browser with an Authorized Device before creating a CLI setup code."
        : "CLI setup code could not be created.");
      return null;
    }
    const snippet = {
      command: body.command,
      code: body.code,
      expires_at: body.expires_at,
      source: body.source ?? source
    };
    setCliBootstrapSnippet(snippet);
    return snippet;
  }, [bootstrap.projectId, bootstrap.workspaceId]);

  React.useEffect(() => {
    if (!signedIn || cliBootstrapSnippet || cliBootstrapMessage || !bootstrap.workspaceId || !getAccountVaultSession(auth.email)) return;
    if (inviteAccepted) {
      void createCliBootstrapCode("invite_acceptance");
      return;
    }
    if (firstUser && flow.keyGenerated) void createCliBootstrapCode("first_run");
  }, [auth.email, bootstrap.workspaceId, cliBootstrapMessage, cliBootstrapSnippet, createCliBootstrapCode, firstUser, flow.keyGenerated, inviteAccepted, signedIn]);

  React.useEffect(() => {
    if (dashboardSection === "members") void loadInvitations();
  }, [dashboardSection, loadInvitations]);

  React.useEffect(() => {
    if (dashboardSection === "machines") void loadDeployTokens();
  }, [dashboardSection, loadDeployTokens]);

  React.useEffect(() => {
    if (dashboardSection === "keyfiles" || dashboardSection === "keys" || dashboardSection === "channels") void loadDeployHistory();
  }, [dashboardSection, loadDeployHistory]);

  React.useEffect(() => {
    setRiskAcknowledgeReady(false);
    setRiskAcknowledgeChecked(false);
    if (!signedIn || accountVaultProductionReady || !bootstrap.workspaceId || !bootstrap.projectId) return;
    const timeout = window.setTimeout(() => setRiskAcknowledgeReady(true), 3000);
    return () => window.clearTimeout(timeout);
  }, [signedIn, accountVaultProductionReady, bootstrap.workspaceId, bootstrap.projectId]);

  const createInvite = async () => {
    setInviteError("");
    setInviteMessage("");
    setInviteResult(null);
    if (!bootstrap.workspaceId) {
      setInviteError("Create a workspace before inviting teammates.");
      return;
    }
    setIsCreatingInvite(true);
    try {
      const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/invitations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteForm.email, role: inviteForm.role, send_email: false })
      });
      const body = await response.json() as { invitation?: InvitationSummary; error?: string; email_delivery?: string; retry_after_seconds?: number };
      if (!response.ok || !body.invitation) throw new Error(rateLimitMessage(body, "Invite could not be created."));
      setInviteResult(body.invitation);
      setInviteMessage("Invite link created. Copy it now, or send an email invite as the next step.");
      setInviteForm({ ...inviteForm, email: "" });
      await loadInvitations();
    } catch (error) {
      setInviteError(error instanceof Error ? error.message : "Invite could not be created.");
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const copyInviteLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setInviteMessage("Invite link copied.");
    } catch {
      setInviteError("Could not copy automatically. Select and copy the link manually.");
    }
  };

  const sendInviteEmail = async (inviteId: string) => {
    setInviteError("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/invitations/${inviteId}/resend`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { invite_url?: string; email_delivery?: string; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.invite_url) {
      setInviteError(rateLimitMessage(body, "Email invite could not be sent."));
      return;
    }
    setInviteMessage(body.email_delivery === "not_configured" ? "Invite link is still ready. Email delivery is blocked until provider credentials are configured." : "Email invite sent.");
    if (inviteResult) setInviteResult({ ...inviteResult, invite_url: body.invite_url });
    await loadInvitations();
  };

  const revokeInvite = async (inviteId: string) => {
    setInviteError("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/invitations/${inviteId}/revoke`, {
      method: "POST",
      credentials: "include"
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setInviteError(rateLimitMessage(body, "Invite could not be revoked."));
      return;
    }
    setInviteMessage("Invite revoked.");
    await loadInvitations();
  };

  const resendInvite = async (inviteId: string) => {
    setInviteError("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/invitations/${inviteId}/resend`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { invite_url?: string; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.invite_url) {
      setInviteError(rateLimitMessage(body, "Invite could not be refreshed."));
      return;
    }
    setInviteMessage("Invite refreshed. Copy the new link.");
    setInviteResult({
      id: inviteId,
      workspace_id: bootstrap.workspaceId,
      email: pendingInvites.find((invite) => invite.id === inviteId)?.email ?? inviteForm.email,
      role: pendingInvites.find((invite) => invite.id === inviteId)?.role ?? "developer",
      status: "pending",
      invite_url: body.invite_url,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date().toISOString()
    });
    await loadInvitations();
  };

  const assignProject = async (userId: string, role: ProjectRole) => {
    setAdminMessage("");
    if (!bootstrap.projectId) {
      setAdminMessage("Create a project before assigning members.");
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/projects/${bootstrap.projectId}/assignments`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, role })
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "Project assignment could not be saved."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Project assignment saved.");
  };

  const removeProjectAssignment = async (userId: string) => {
    setAdminMessage("");
    if (!bootstrap.projectId) {
      setAdminMessage("Create a project before removing assignments.");
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/projects/${bootstrap.projectId}/assignments/${userId}/remove`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "Project assignment could not be removed."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Project assignment removed.");
  };

  const updateMemberRole = async (userId: string, role: WorkspaceRole) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/members/${userId}/role`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setAdminMessage(rateLimitMessage(body, "Member role could not be updated."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Member role updated.");
  };

  const deactivateMember = async (userId: string) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/members/${userId}/deactivate`, {
      method: "POST",
      credentials: "include"
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setAdminMessage(rateLimitMessage(body, "Member could not be deactivated."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Member deactivated.");
  };

  const createChannel = async () => {
    setAdminMessage("");
    const bundle = dashboardState?.bundles[0];
    const project = dashboardState?.projects[0];
    if (!bundle || !project) {
      setAdminMessage("Create a project and bundle before adding a channel.");
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/channels`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspace_id: bootstrap.workspaceId, project_id: project.id, bundle_id: bundle.id, name: channelDraft })
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "Channel could not be created."));
      return;
    }
    setChannelDraft("");
    await refreshDashboardState();
    setAdminMessage("Channel created.");
  };

  const grantChannel = async () => {
    setAdminMessage("");
    const channelId = grantDraft.channelId || selectedChannelRecord?.id;
    if (!channelId || !grantDraft.keysetId) {
      setAdminMessage("Choose a channel and registered public metadata before granting access.");
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/channel-grants`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel_id: channelId, keyset_id: grantDraft.keysetId, preset: grantDraft.preset })
    });
    const body = await response.json().catch(() => ({})) as { error?: string; grant_status?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "Channel grant could not be saved."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage(`Grant saved: ${body.grant_status ?? "active"}.`);
  };

  const revokeGrant = async (grantId: string) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/channel-grants/${grantId}/revoke`, {
      method: "POST",
      credentials: "include"
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setAdminMessage(rateLimitMessage(body, "Grant could not be revoked."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Grant revoked. Future publishes will exclude that key.");
  };

  const updateGrantPreset = async (grantId: string, preset: string) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/channel-grants/${grantId}/update`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preset })
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "Grant preset could not be updated."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Grant preset updated.");
  };

  const rollbackVersion = async (channelId: string, versionId: string) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/channels/${channelId}/rollback`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version_id: versionId })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setAdminMessage(rateLimitMessage(body, "Version rollback could not be applied."));
      return;
    }
    await refreshDashboardState();
    setAdminMessage("Channel pointer rolled back to the selected immutable version.");
  };

  const createMachine = async () => {
    setMachineMessage("");
    if (!bootstrap.workspaceId || !selectedChannel) {
      setMachineMessage("Create a workspace and channel before creating a machine identity.");
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/v1/machines`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: bootstrap.workspaceId,
          name: machineName || "envship-machine",
          signing_key_id: `msig_browser_${Date.now()}`,
          signing_public_jwk: { kty: "EC", crv: "P-256", kid: `msig_browser_${Date.now()}` },
          channels: [selectedChannel]
        })
      });
      const body = await response.json() as { machine_id?: string; error?: string; retry_after_seconds?: number };
      if (!response.ok || !body.machine_id) throw new Error(rateLimitMessage(body, "Machine could not be created."));
      setMachineMessage(`Machine created for ${selectedChannel}: ${body.machine_id}`);
      await refreshDashboardState();
    } catch (error) {
      setMachineMessage(error instanceof Error ? error.message : "Machine could not be created.");
    }
  };

  const createDeployToken = async () => {
    setMachineMessage("");
    setDeployTokenResult(null);
    if (!bootstrap.projectId || !selectedChannel) {
      setMachineMessage("Create a project and channel before creating a deploy token.");
      return;
    }
    const response = await fetch(`${apiBaseUrl}/v1/projects/${bootstrap.projectId}/deploy-tokens`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: deployTokenName || "Deploy token", channels: [selectedChannel] })
    });
    const body = await response.json().catch(() => ({})) as { deploy_token?: { token: string; channels: string[] }; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.deploy_token) {
      setMachineMessage(rateLimitMessage(body, "Deploy token could not be created."));
      return;
    }
    setDeployTokenResult({ token: body.deploy_token.token, channels: body.deploy_token.channels });
    setMachineMessage("Deploy token created. Copy it now; EnvShip stores only a hash.");
    await refreshDashboardState();
    await loadDeployTokens();
  };

  const revokeDeployToken = async (tokenId: string) => {
    setMachineMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/deploy-tokens/${encodeURIComponent(tokenId)}/revoke`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setMachineMessage(rateLimitMessage(body, "Deploy token could not be revoked."));
      return;
    }
    await refreshDashboardState();
    await loadDeployTokens();
    setMachineMessage("Deploy token revoked.");
  };

  const rotateDeployToken = async (tokenId: string) => {
    setMachineMessage("");
    setDeployTokenResult(null);
    const response = await fetch(`${apiBaseUrl}/v1/deploy-tokens/${encodeURIComponent(tokenId)}/rotate`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { deploy_token?: { token: string; channels: string[] }; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.deploy_token) {
      setMachineMessage(rateLimitMessage(body, "Deploy token could not be rotated."));
      return;
    }
    setDeployTokenResult({ token: body.deploy_token.token, channels: body.deploy_token.channels });
    await refreshDashboardState();
    await loadDeployTokens();
    setMachineMessage("Deploy token rotated. Copy the new value now; EnvShip stores only a hash.");
  };

  const updateKeysetStatus = async (keysetId: string, status: "revoked" | "lost") => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/keysets/${encodeURIComponent(keysetId)}/status`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspace_id: bootstrap.workspaceId, status })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setAdminMessage(rateLimitMessage(body, `Vault metadata could not be marked ${status}.`));
      return;
    }
    await refreshDashboardState();
    setAdminMessage(`Vault metadata marked ${status}. Future publishes will exclude it.`);
  };

  const markAuthorizedDevice = async (credentialIdB64u: string, status: "revoked" | "lost") => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/authorized-devices/${encodeURIComponent(credentialIdB64u)}/${status === "lost" ? "mark-lost" : "revoke"}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) {
      setAdminMessage(`Authorized Device could not be marked ${status}.`);
      return;
    }
    await refreshKeyfileBackups();
    setAdminMessage(`Authorized Device marked ${status}.`);
  };

  const acknowledgeOneDeviceRisk = async () => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/authorized-device-acknowledgements`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        workspace_id: bootstrap.workspaceId,
        project_id: bootstrap.projectId,
        device_count: authorizedDevices.filter((device) => device.status === "active").length,
        message_version: "phase2.one-device-admin-risk.v1"
      })
    });
    if (!response.ok) {
      setAdminMessage("Authorized Device risk acknowledgement could not be recorded.");
      return;
    }
    setAdminMessage("Authorized Device risk acknowledgement recorded.");
  };

  const revokeMachine = async (machineId: string) => {
    setMachineMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/machines/${machineId}/revoke`, {
      method: "POST",
      credentials: "include"
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
      setMachineMessage(rateLimitMessage(body, "Machine could not be revoked."));
      return;
    }
    await refreshDashboardState();
    setMachineMessage("Machine revoked and pull-resolution rows were removed.");
  };

  const loadMachineInstructions = async (machineId: string) => {
    setMachineMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/machines/${machineId}/install-instructions`, {
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { snippets?: Record<string, string>; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.snippets) {
      setMachineMessage(rateLimitMessage(body, "Machine install instructions could not be loaded."));
      return;
    }
    setMachineInstructions(Object.entries(body.snippets).map(([key, value]) => `${key}: ${value}`).join("\n"));
  };

  const addPasskeyUnlockToVault = async () => {
    setEditorMessage("");
    const keyset = unlockedKeyset ?? lastGeneratedKeyFile?.keyset;
    if (!keyset) {
      setEditorMessage("Unlock the EnvShip vault before adding passkey unlock.");
      return;
    }
    const currentBundle = getAccountVaultSession(auth.email)?.encryptedKeyBundle ?? keyfileBackups[0]?.encrypted_key_bundle;
    if (!currentBundle) {
      setEditorMessage("Encrypted account vault was not loaded.");
      return;
    }
    try {
      const passkey = await registerPasskeyForVault(apiBaseUrl, keyset.label);
      if (!passkey.prfOutput) throw new Error("This passkey did not return PRF unlock material. EnvShip Dashboard requires PRF-capable Authorized Devices.");
      const encryptedBundle = await upsertPrfWrapForKeyBundle({
        bundle: currentBundle,
        keyset,
        credentialIdB64u: passkey.credentialIdB64u,
        prfOutput: passkey.prfOutput
      });
      const response = await fetch(`${apiBaseUrl}/v1/account-vault`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encrypted_key_bundle: encryptedBundle })
      });
      if (!response.ok) throw new Error("Passkey unlock backup could not be saved.");
      if (auth.email) setAccountVaultSession({ email: auth.email, keyset, encryptedKeyBundle: encryptedBundle, credentialIdB64u: passkey.credentialIdB64u });
      await refreshKeyfileBackups();
      setEditorMessage(`Passkey device added for ${keyset.label}. Phase 2 makes this the default unlock path.`);
      setKeyActionMessage("Passkey device added. Phase 2 makes Authorized Devices the default launch unlock path.");
    } catch (error) {
      setEditorMessage(error instanceof Error ? error.message : "Passkey unlock could not be added.");
      setKeyActionMessage(error instanceof Error ? error.message : "Passkey unlock could not be added.");
    }
  };

  const clearUnlockedKeyFile = () => {
    setUnlockedKeyset(null);
    setEditorText("");
    setEditorExpectedVersionId(null);
    setEditorBundleHash("");
    setEditorMessage("Browser vault session cleared.");
  };

  const loadEditorChannel = async () => {
    setEditorMessage("");
    if (!unlockedKeyset) {
      setEditorMessage("Unlock the browser vault before loading encrypted env values.");
      return;
    }
    if (!editorChannel) {
      setEditorMessage("Choose a channel before loading env values.");
      return;
    }
    setIsEditorBusy(true);
    try {
      const resolutionResponse = await fetch(`${apiBaseUrl}/v1/channels/${editorChannel.id}/current-bundle`, {
        credentials: "include"
      });
      const resolution = await resolutionResponse.json().catch(() => ({})) as {
        error?: string;
        version_id?: string;
        version?: number;
        bundle_sha256_b64u?: string;
        encrypted_bundle?: EncryptedEnvBundle;
        retry_after_seconds?: number;
      };
      if (!resolutionResponse.ok || !resolution.encrypted_bundle || !resolution.bundle_sha256_b64u || !resolution.version_id) {
        throw new Error(rateLimitMessage(resolution, "Current encrypted version could not be resolved."));
      }
      const hash = await cryptoSha256Base64url(cryptoCanonicalize(resolution.encrypted_bundle));
      if (hash !== resolution.bundle_sha256_b64u) throw new Error("Encrypted bundle hash mismatch.");
      const env = await decryptEnvBundle(resolution.encrypted_bundle, unlockedKeyset);
      setEditorText(stringifyDotenv(env));
      setEditorExpectedVersionId(resolution.version_id);
      setEditorBundleHash(hash);
      setEditorMessage(`Loaded ${editorChannel.channel_ref} version ${resolution.version ?? resolution.version_id.slice(0, 8)} for local editing.`);
    } catch (error) {
      setEditorMessage(error instanceof Error ? error.message : "Current channel could not be decrypted.");
    } finally {
      setIsEditorBusy(false);
    }
  };

  const publishEditorChannel = async (mode: "edit" | "rewrap") => {
    setEditorMessage("");
    if (!unlockedKeyset) {
      setEditorMessage("Unlock the browser vault before publishing.");
      return;
    }
    if (!editorChannel) {
      setEditorMessage("Choose a channel before publishing.");
      return;
    }
    if (!editorExpectedVersionId && mode === "edit") {
      setEditorMessage("Load the current encrypted version before saving an edit.");
      return;
    }
    setIsEditorBusy(true);
    try {
      const env = parseDotenv(editorText);
      const recipientsResponse = await fetch(`${apiBaseUrl}/v1/channels/${editorChannel.id}/recipients`, {
        credentials: "include"
      });
      const recipientsBody = await recipientsResponse.json().catch(() => ({})) as {
        error?: string;
        expected_current_version_id?: string | null;
        recipients?: Array<{ encryption_key_id: string; encryption_public_jwk: JsonWebKey }>;
        retry_after_seconds?: number;
      };
      if (!recipientsResponse.ok || !recipientsBody.recipients?.length) {
        throw new Error(rateLimitMessage(recipientsBody, "Active recipients could not be loaded."));
      }
      const expectedCurrentVersionId = mode === "rewrap"
        ? recipientsBody.expected_current_version_id ?? editorChannel.current_version_id ?? null
        : editorExpectedVersionId;
      if (!expectedCurrentVersionId && editorChannel.current_version_id) {
        throw new Error("Current version changed. Reload the channel before publishing.");
      }
      const encryptedBundle = await encryptEnvBundleForRecipients(env, recipientsBody.recipients, editorChannel.channel_ref);
      const bundleHash = await cryptoSha256Base64url(cryptoCanonicalize(encryptedBundle));
      const manifest = await signManifest({
        type: "envship.signed_manifest.v1",
        channel_ref: editorChannel.channel_ref,
        bundle_sha256_b64u: bundleHash,
        publisher_signing_key_id: unlockedKeyset.signing_key_id,
        created_at: new Date().toISOString()
      }, unlockedKeyset);
      const publishResponse = await fetch(`${apiBaseUrl}/v1/versions/publish`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel_id: editorChannel.id,
          expected_current_version_id: expectedCurrentVersionId,
          publisher_signing_key_id: unlockedKeyset.signing_key_id,
          manifest,
          encrypted_bundle: encryptedBundle,
          bundle_sha256_b64u: bundleHash
        })
      });
      const publishBody = await publishResponse.json().catch(() => ({})) as {
        error?: string;
        current_version_id?: string | null;
        version_id?: string;
        version?: number;
        deploy_id?: string;
        deploy_status?: DeployOperation["status"];
        deploy_checklist?: DeployChecklistItem[];
        retry_after_seconds?: number;
      };
      if (publishResponse.status === 409) {
        setEditorExpectedVersionId(publishBody.current_version_id ?? null);
        throw new Error("Current version changed before publish. Reload the channel and apply your edit again.");
      }
      if (!publishResponse.ok || !publishBody.version_id) {
        throw new Error(rateLimitMessage(publishBody, "Encrypted version could not be published."));
      }
      setEditorExpectedVersionId(publishBody.version_id);
      setEditorBundleHash(bundleHash);
      await refreshDashboardState();
      if (publishBody.deploy_id) {
        const deploy = await loadDeployDetail(publishBody.deploy_id);
        setActiveDeploy(deploy ?? {
          id: publishBody.deploy_id,
          channel_ref: editorChannel.channel_ref,
          version_id: publishBody.version_id,
          version: publishBody.version,
          operation_type: mode === "rewrap" ? "encrypted_access_update" : "publish",
          status: publishBody.deploy_status ?? "saved",
          checklist: publishBody.deploy_checklist ?? [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        void loadDeployHistory();
      }
      setEditorMessage(`${mode === "rewrap" ? "Updated encrypted access for" : "Published"} ${editorChannel.channel_ref} as version ${publishBody.version ?? publishBody.version_id}. Deploy status is being validated.`);
    } catch (error) {
      setEditorMessage(error instanceof Error ? error.message : "Encrypted version could not be published.");
    } finally {
      setIsEditorBusy(false);
    }
  };

  const retryDeployPurge = async (deployId: string) => {
    setDeployStatusMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/deploys/${deployId}/retry-purge`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { deploy?: DeployOperation; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.deploy) {
      setDeployStatusMessage(rateLimitMessage(body, "CDN refresh retry could not be started."));
      return;
    }
    setActiveDeploy(body.deploy);
    setDeployHistory((current) => [body.deploy as DeployOperation, ...current.filter((deploy) => deploy.id !== body.deploy?.id)].slice(0, 25));
    setDeployStatusMessage(body.deploy.status === "deploy_ready" ? "Deploy is ready for runtime pulls." : "CDN refresh retry recorded. EnvShip will keep retrying in the background.");
  };

  const subscribeDeployNotification = async (deployId: string) => {
    setDeployStatusMessage("");
    let permission = "granted";
    if ("Notification" in window && Notification.permission !== "granted") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
      setDeployStatusMessage("Browser notifications were not enabled. You can refresh deploy history manually.");
      return;
    }
    const endpoint = `https://envship.local/push/${deployId}/${crypto.randomUUID()}`;
    const response = await fetch(`${apiBaseUrl}/v1/notifications/web-push/subscribe`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        deploy_id: deployId,
        subscription: {
          endpoint,
          keys: { p256dh: "local-dev-placeholder", auth: "local-dev-placeholder" }
        }
      })
    });
    const body = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) {
      setDeployStatusMessage(body.error ?? "Deploy notification subscription failed.");
      return;
    }
    setDeployStatusMessage("Push notification enabled for this deploy readiness state.");
    await loadDeployDetail(deployId);
  };

  const exportAudit = async () => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/audit/export`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { export_id?: string; error?: string; retry_after_seconds?: number };
    if (!response.ok || !body.export_id) {
      setAdminMessage(rateLimitMessage(body, "Audit export could not be created."));
      return;
    }
    setAdminMessage(`Audit export created: ${body.export_id}`);
    await refreshDashboardState();
  };

  const openBillingCheckout = async (checkoutBody: Record<string, unknown> = { plan: "pro" }) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/billing/checkout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(checkoutBody)
    });
    const responseBody = await response.json().catch(() => ({})) as { error?: string; url?: string; retry_after_seconds?: number };
    if (response.ok && responseBody.url) {
      window.location.href = responseBody.url;
      return;
    }
    setAdminMessage(rateLimitMessage(responseBody, `PayPal subscription checkout could not be opened. Receiver: ${paypalReceiverEmail}. IPN: https://api.envship.com/v1/billing/paypal/ipn.`));
  };

  const revokeCliWorkstation = async (workstationId: string) => {
    setAdminMessage("");
    const response = await fetch(`${apiBaseUrl}/v1/workspaces/${bootstrap.workspaceId}/cli-workstations/${workstationId}/revoke`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { error?: string; retry_after_seconds?: number };
    if (!response.ok) {
      setAdminMessage(rateLimitMessage(body, "CLI Workstation could not be revoked."));
      return;
    }
    setAdminMessage("CLI Workstation revoked.");
    await refreshDashboardState();
  };

  const openStep = (step: DashboardStep) => saveFlow({ ...flow, expandedStep: step });
  const stepState = (step: DashboardStep) => {
    return resolveStepState(flow, step);
  };
  const isExpanded = (step: DashboardStep) => flow.expandedStep === step;
  const stepSummary = (step: DashboardStep) => {
    if (step === "workspace" && !flow.workspaceConfigured) return "Create the workspace, project, bundle, and starter channels";
    if (step === "workspace" && flow.workspaceConfigured) return `Workspace: ${bootstrap.workspaceName} - Project: ${bootstrap.projectName} - Channels: ${bootstrap.channels.map((channel) => `${bootstrap.bundleName}/${channel}`).join(", ")}`;
    if (step === "keyfile") return flow.keyDownloadConfirmed ? `Encrypted vault saved: ${flow.keyLabel}` : flow.keyGenerated ? "Encrypted vault created; continue when ready" : "Generate the encrypted private backup";
    if (step === "register") return flow.publicRegistered ? `Public key registered: ${flow.publicKeyId}` : "Register public metadata only";
    if (step === "grant") return flow.grantConfirmed ? `Granted ${selectedChannel}: decrypt, edit, publish` : `Self-grant first admin access to ${selectedChannel}`;
    return flow.versionPublished ? "Current version includes this key" : "Publish the first encrypted version";
  };
  const lockedReason = (step: DashboardStep) => {
    if (step === "keyfile") return "Save the workspace setup first.";
    if (step === "register") return "Create the encrypted vault before registering public metadata.";
    if (step === "grant") return "Register public metadata before granting channel access.";
    return "Grant staging access before publishing the first version.";
  };
  const StepCard = (props: { step: DashboardStep; number: string; title: string; children: React.ReactNode }) => {
    const state = stepState(props.step);
    const expanded = isExpanded(props.step);
    return (
      <article className={`setup-step ${state} ${expanded ? "expanded" : ""}`}>
        <button
          type="button"
          className="setup-step-header"
          data-step-open={props.step}
          aria-disabled={state === "locked"}
          onClick={() => {
            if (state !== "locked") openStep(props.step);
          }}
          disabled={state === "locked"}
        >
          <span className="step-number">{props.number}</span>
          <span>
            <strong>{props.title}</strong>
            <small>{state === "locked" ? lockedReason(props.step) : stepSummary(props.step)}</small>
          </span>
          <em>{state === "complete" ? "Complete" : state === "locked" ? "Locked" : "Active"}</em>
        </button>
        {expanded && state !== "locked" && <div className="setup-step-body">{props.children}</div>}
      </article>
    );
  };
  const sectionMeta: Record<string, { eyebrow: string; title: string; text: string; items: string[] }> = {
    keyfiles: {
      eyebrow: "Vaults and Authorized Devices",
      title: "Unlock, decrypt, sign, and publish without server-side secrets.",
      text: "Dashboard uses an encrypted browser vault shell now, with account-level passkey PRF Authorized Devices replacing temporary unlock paths in Phase 2.",
      items: ["Unlock encrypted vault backup", "Add passkey device explicitly", "Track grant state"]
    },
    keys: {
      eyebrow: "Vaults and Authorized Devices",
      title: "Unlock, decrypt, sign, and publish without server-side secrets.",
      text: "Dashboard uses an encrypted browser vault shell now, with account-level passkey PRF Authorized Devices replacing temporary unlock paths in Phase 2.",
      items: ["Unlock encrypted vault backup", "Add passkey device explicitly", "Track grant state"]
    },
    members: {
      eyebrow: "Members",
      title: "Invite teammates without handing out production secrets.",
      text: "Workspace roles control admin access. Project assignment controls app visibility. Channel grants control exact env access.",
      items: ["Invite by email", "Assign owner/admin/developer/viewer roles", "Assign project managers and developers", "Review pending public-key grant requests"]
    },
    channels: {
      eyebrow: "Channels",
      title: "Stable deploy lanes with exact access boundaries.",
      text: "Channels such as web/staging and web/production keep scripts stable while immutable encrypted versions move behind the pointer.",
      items: ["Create staging and production lanes", "Grant decrypt/edit/publish per channel", "Publish immutable versions", "Rollback to known encrypted versions"]
    },
    machines: {
      eyebrow: "Machines",
      title: "Scoped identities for CI, Docker, and VPS pulls.",
      text: "Machines are deploy identities with explicit channel grants. They can pull only the channels assigned to them.",
      items: ["Each online server uses one machine seat", "Autoscaling and fresh instances are supported", "Limit machines to specific channels", "Revoke old runners or servers"]
    },
    audit: {
      eyebrow: "Audit",
      title: "Safe history for access reviews and incident response.",
      text: "Ledger records metadata about grants, revocations, publishes, rollbacks, and machine activity without storing plaintext env values.",
      items: ["Review key registration", "Review channel grants", "Review publish and rollback history", "Export evidence for access reviews"]
    },
    billing: {
      eyebrow: "Billing",
      title: "Plan limits tied to workspaces, bundles, channels, and machines.",
      text: "Billing controls capacity without changing the cryptographic boundary. Paid plans add retention, machines, larger bundles, and team controls.",
      items: ["View current plan", "Machine seats are counted per online server", "Over-limit machine usage gets a 7-day upgrade grace", "IPN ready at /v1/billing/paypal/ipn"]
    }
  };
  const navItems = [
    { label: "Overview", key: "overview", href: "/dashboard", enabled: true },
    { label: "Vault", key: "keyfiles", href: "/dashboard/keyfiles", enabled: hasActiveWorkspace },
    { label: "Members", key: "members", href: "/dashboard/members", enabled: hasActiveWorkspace },
    { label: "Channels", key: "channels", href: "/dashboard/channels", enabled: hasActiveWorkspace },
    { label: "Machines", key: "machines", href: "/dashboard/machines", enabled: hasActiveWorkspace },
    { label: "Audit", key: "audit", href: "/dashboard/audit", enabled: hasActiveWorkspace },
    { label: "Billing", key: "billing", href: "/dashboard/billing", enabled: hasActiveWorkspace }
  ];
  const setupEvents = [
    flow.workspaceConfigured && `Workspace created: ${bootstrap.workspaceName}`,
    flow.keyGenerated && `Authorized Device shell created: ${flow.keyLabel}`,
    flow.keyDownloadConfirmed && "Encrypted vault confirmed",
    flow.publicRegistered && `Public key registered: ${flow.publicKeyId}`,
    flow.grantConfirmed && `Granted ${selectedChannel}`,
    flow.versionPublished && "First encrypted version published"
  ].filter(Boolean) as string[];
  const activeSection = sectionMeta[dashboardSection];
  const sectionNextStep = dashboardSection === "audit" && setupEvents.length
    ? `Current setup evidence: ${setupEvents.join(" - ")}.`
    : dashboardSection === "keyfiles" || dashboardSection === "keys"
      ? flow.keyDownloadConfirmed
        ? "Public metadata can now be registered and granted to exact channels."
        : "Go to Overview and add the first Authorized Device shell before registering public metadata."
      : dashboardSection === "members"
        ? "Invite a teammate, assign a project, then grant only the channels they need."
        : dashboardSection === "channels"
          ? "Create or review deploy lanes such as web/staging and web/production, then grant capabilities per lane."
          : dashboardSection === "machines"
            ? "Machine identities should receive pull-only access to the exact channels they deploy."
            : dashboardSection === "billing"
              ? "Start on Free, then upgrade when projects, bundles, channels, machines, retention, or team seats grow."
              : "Use Overview to finish setup before moving deeper into workspace administration.";
  const sectionPrimaryAction = dashboardSection === "keyfiles" || dashboardSection === "keys"
    ? flow.keyDownloadConfirmed ? "Review vault status" : "Create vault in Overview"
    : dashboardSection === "members"
      ? "Invite teammate"
      : dashboardSection === "channels"
        ? `Manage ${selectedChannel}`
        : dashboardSection === "machines"
          ? "Create machine identity"
          : dashboardSection === "audit"
            ? "Review audit evidence"
            : dashboardSection === "billing"
              ? "Review plan limits"
              : "Go to Overview";
  const sectionRows = dashboardSection === "keyfiles" || dashboardSection === "keys"
    ? (dashboardState?.keysets.length ? dashboardState.keysets.map((keyset) => `${keyset.label}: ${keyset.status} (${keyset.signing_key_id})`) : sectionMeta.keyfiles.items)
    : dashboardSection === "channels"
      ? (dashboardState?.channels.length ? dashboardState.channels.map((channel) => {
        const grants = dashboardState.grants.filter((grant) => grant.channel_id === channel.id).length;
        return `${channel.channel_ref}: version ${channel.current_version ?? "none"} - ${grants} grant${grants === 1 ? "" : "s"}`;
      }) : sectionMeta.channels.items)
      : dashboardSection === "machines"
        ? (dashboardState?.machines.length ? dashboardState.machines.map((machine) => `${machine.name}: ${machine.status} - ${machine.channels.join(", ") || "no channels"}`) : sectionMeta.machines.items)
        : dashboardSection === "audit"
          ? (dashboardState?.audit_events.length ? dashboardState.audit_events.map((event) => `${event.action}: ${event.subject_type} - ${new Date(event.created_at).toLocaleString()}`) : sectionMeta.audit.items)
          : dashboardSection === "billing"
            ? [`Plan: ${dashboardState?.workspaces[0]?.plan ?? "free"}`, ...sectionMeta.billing.items]
            : (activeSection?.items ?? ["Workspace-scoped data", "Role-aware access", "Audit-safe metadata", "No plaintext env values"]);
  const visibleSectionRows = auditFilter.trim()
    ? sectionRows.filter((item) => item.toLowerCase().includes(auditFilter.trim().toLowerCase()))
    : sectionRows;

  return (
    <main className="dashboard-app">
      <header className="dashboard-topbar">
        <a className="brand" href="/"><span className="brand-mark" />EnvShip</a>
        <div className="topbar-selectors">
          <label><small>Workspace</small>
            <select value={bootstrap.workspaceId} onChange={(event) => {
              const workspace = dashboardState?.workspaces.find((item) => item.id === event.target.value);
              if (workspace) setBootstrap({ ...bootstrap, workspaceId: workspace.id, workspaceName: workspace.name, workspaceRole: workspace.role });
            }}>
              {dashboardState?.workspaces.length ? dashboardState.workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>) : <option value="">Not created yet</option>}
            </select>
          </label>
          <label><small>Project</small>
            <select value={bootstrap.projectId} onChange={(event) => {
              const project = dashboardState?.projects.find((item) => item.id === event.target.value);
              if (project) setBootstrap({ ...bootstrap, projectId: project.id, projectName: project.name });
            }}>
              {dashboardState?.projects.length ? dashboardState.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>) : <option value="">Pending setup</option>}
            </select>
          </label>
          <a className="secondary-action compact" href="/dashboard">Manage workspaces</a>
          <button type="button" className="secondary-action compact" onClick={createAnotherWorkspace}>Add workspace</button>
          <a className="secondary-action compact" href="/dashboard">Manage projects</a>
          <button type="button" className="secondary-action compact" onClick={editWorkspaceSetup}>Add project</button>
        </div>
        {signedIn ? <button className="secondary-action compact" onClick={signOut}>{sessionEmail} - Sign out</button> : null}
      </header>
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <nav className="dashboard-nav">
            {navItems.map((item) => {
              if (!item.enabled) {
                return (
                  <span className="locked-nav" key={item.key}>
                    {item.label}
                    <small>Create workspace first</small>
                  </span>
                );
              }
              return <a className={dashboardSection === item.key ? "active" : ""} href={item.href} key={item.key}>{item.label}</a>;
            })}
          </nav>
        </aside>
        <section className="dashboard-main">
          {auth.loading && (
            <section className="dashboard-card">
              <h1>Checking session</h1>
              <p>Dashboard is asking the local EnvShip API for your signed-in session.</p>
            </section>
          )}
          {!auth.loading && !signedIn && (
            <section className="dashboard-card">
              <h1>Sign in to open Dashboard</h1>
              <p>Login loads workspace metadata. It does not unlock env values, decrypt bundles, or upload private keys.</p>
              {auth.error && <div className="callout compact">{auth.error}</div>}
              <a className="button primary" href="/login?return_to=/dashboard">Sign in</a>
            </section>
          )}
          {!auth.loading && signedIn && firstUser && dashboardSection === "overview" && (
            <section className="dashboard-card setup-wizard">
              <div className="dashboard-card-header">
                <div>
                  <p className="eyebrow">First admin setup</p>
                  <h1>Create your first project workspace.</h1>
                  <p>Choose your use case, create the workspace and project boundary, keep Free selected unless you are ready for PayPal subscription checkout, then add the first Authorized Device shell for browser work.</p>
                </div>
                  <button type="button" className="secondary-action compact" onClick={() => void runAction("setup:reset", resetFlow)} disabled={isPending("setup:reset")}>{isPending("setup:reset") ? "Resetting..." : "Reset setup"}</button>
              </div>
              <div className="setup-steps">
                <StepCard step="workspace" number="1" title="Choose use case and create workspace">
                  <p>This creates the team boundary that owns projects, bundles, channel grants, machines, billing, and audit history. It still does not contain plaintext env values.</p>
                  <div className="segmented-choice" aria-label="Use case">
                    <button type="button" className="selected">Developer</button>
                    <button type="button">Company/team</button>
                  </div>
                  <div className="field-grid">
                    <label>Workspace<input value={setupDraft.workspaceName} onChange={(event) => setSetupDraft({ ...setupDraft, workspaceName: event.target.value })} /></label>
                    <label>Project<input value={setupDraft.projectName} onChange={(event) => setSetupDraft({ ...setupDraft, projectName: event.target.value })} /></label>
                    <label>Bundle<input value={setupDraft.bundleName} onChange={(event) => setSetupDraft({ ...setupDraft, bundleName: event.target.value })} /></label>
                    <label>Channels<input value={setupDraft.channels} onChange={(event) => setSetupDraft({ ...setupDraft, channels: event.target.value })} /></label>
                  </div>
                  <button type="button" className="button primary" onClick={() => void runAction("setup:workspace", saveWorkspaceSetup)} disabled={isPending("setup:workspace")}>{isPending("setup:workspace") ? "Saving..." : flow.workspaceConfigured ? "Save workspace details" : "Create workspace and project"}</button>
                </StepCard>
                <StepCard step="keyfile" number="2" title="Choose Free plan and confirm Authorized Device">
                  <p>Free includes 1 workspace, 1 project, 1 bundle, 2 channels, browser Dashboard access, 1 CLI Workstation per user, and 2 machine seats. Your account vault was unlocked with passkey PRF before Dashboard opened.</p>
                  <div className="plan-choice"><strong>Free selected</strong><span>Start Free now. Owners can open generated PayPal subscription checkout from Billing after workspace setup.</span></div>
                  <div className="field-grid">
                    <label>Vault label<input value={flow.keyLabel} onChange={(event) => updateFlow({ keyLabel: event.target.value })} /></label>
                  </div>
                  <div className="warning-panel"><strong>Add a second Authorized Device before production admin work.</strong> EnvShip stores only encrypted key bundles and cannot recover plaintext secrets if every Authorized Device is lost.</div>
                  {flow.keyGenerated && (
                    <div className="download-confirm">
                      <strong>Your encrypted account vault is saved.</strong>
                      <p>The first Authorized Device is ready. Add another Authorized Device before production-ready admin work.</p>
                      <div className="callout compact">
                        <strong>Paste this in your development environment to instantly set up the EnvShip CLI.</strong>
                        <p>Optional. You can skip this and connect the CLI later. A CLI Workstation is one trusted coding environment; browser Dashboard access is included.</p>
                        {cliBootstrapSnippet ? (
                          <>
                            <div className="code-panel">{cliBootstrapSnippet.command}</div>
                            <small>Expires {new Date(cliBootstrapSnippet.expires_at).toLocaleTimeString()} and can be used once.</small>
                          </>
                        ) : (
                          <button type="button" className="button" onClick={() => void runAction("cli-bootstrap:first-run", () => createCliBootstrapCode("first_run"))} disabled={isPending("cli-bootstrap:first-run")}>
                            {isPending("cli-bootstrap:first-run") ? "Creating code..." : "Create CLI setup code"}
                          </button>
                        )}
                        {cliBootstrapMessage && <p className="error-text">{cliBootstrapMessage}</p>}
                      </div>
                      <button type="button" className="button primary" onClick={() => void runAction("keyfile:confirm", () => advanceFlow("confirm-download"))} disabled={isPending("keyfile:confirm")}>Continue</button>
                    </div>
                  )}
                  {keyActionMessage && <p className={flow.keyGenerated ? "success-text" : "error-text"}>{keyActionMessage}</p>}
                  {!flow.keyGenerated && <button type="button" className="button primary" onClick={() => void runAction("keyfile:generate", () => advanceFlow("key"))} disabled={isPending("keyfile:generate")}>{isPending("keyfile:generate") ? "Confirming..." : "Confirm Authorized Device"}</button>}
                </StepCard>
                <StepCard step="register" number="3" title="Create project access metadata">
                  <p>Only public key ids and public JWK metadata are registered. The encrypted private vault stays encrypted, and plaintext key material stays in browser memory only while unlocked.</p>
                  <div className="key-metadata">
                    <code>Encryption key: {flow.publicKeyId || "not generated"}</code>
                    <code>Signing key: {flow.signingKeyId || "not generated"}</code>
                  </div>
                  <button type="button" className="button primary" onClick={() => void runAction("keyfile:register", () => advanceFlow("register"))} disabled={!flow.keyDownloadConfirmed || flow.publicRegistered || isPending("keyfile:register")}>{isPending("keyfile:register") ? "Registering..." : "Register public key metadata"}</button>
                </StepCard>
                <StepCard step="grant" number="4" title="Set bundle/channel access">
                  <p>As first admin, self-grant exact capabilities for {selectedChannel}. Production stays separate until explicitly granted.</p>
                  <button type="button" className="button primary" onClick={() => void runAction("keyfile:grant", () => advanceFlow("grant"))} disabled={!flow.publicRegistered || flow.grantConfirmed || isPending("keyfile:grant")}>{isPending("keyfile:grant") ? "Granting..." : `Grant ${selectedChannel}`}</button>
                </StepCard>
                <StepCard step="publish" number="5" title="Publish first encrypted version">
                  <p>The current encrypted version must include your key. Publishing creates the first signed, immutable version behind {selectedChannel}.</p>
                  <button type="button" className="button primary" onClick={() => void runAction("keyfile:publish", () => advanceFlow("publish"))} disabled={!flow.grantConfirmed || flow.versionPublished || isPending("keyfile:publish")}>{isPending("keyfile:publish") ? "Publishing..." : "Publish first encrypted version"}</button>
                </StepCard>
              </div>
            </section>
          )}
          {!auth.loading && signedIn && dashboardSection !== "overview" && !hasActiveWorkspace && (
            <section className="dashboard-card locked-section">
              <p className="eyebrow">Workspace required</p>
              <h1>Create or join a workspace first.</h1>
              <p>{activeSection?.eyebrow ?? "This section"} belongs to a workspace. Until you create your first workspace or accept an invite, there are no members, channels, machines, audit events, or billing settings to manage.</p>
              <a className="button primary" href="/dashboard?first_user=1">Create first workspace</a>
            </section>
          )}
          {!auth.loading && signedIn && dashboardSection !== "overview" && hasActiveWorkspace && (
            <section className="dashboard-card section-panel">
              <div className="section-heading-row">
                <div>
                  <p className="eyebrow">{activeSection?.eyebrow ?? "Dashboard"}</p>
                  <h1>{activeSection?.title ?? "Dashboard section"}</h1>
                  <p>{activeSection?.text ?? "This dashboard section is scoped to the active workspace."}</p>
                </div>
                {dashboardSection === "members"
                  ? <span className="grant-chip good">Invite flow active</span>
                  : <span className="grant-chip">{sectionPrimaryAction}</span>}
              </div>
              <div className="section-next">
                <strong>How to use this page</strong>
                <p>{sectionNextStep}</p>
              </div>
              {dashboardSection === "members" && inviteAccepted && (
                <div className="callout compact">
                  <strong>Paste this in your development environment to instantly set up the EnvShip CLI.</strong>
                  <p>Optional. You can skip this and connect the CLI later. This CLI Workstation matches the permissions granted by your invite and project assignments; browser Dashboard access is included.</p>
                  {cliBootstrapSnippet ? (
                    <>
                      <div className="code-panel">{cliBootstrapSnippet.command}</div>
                      <small>Expires {new Date(cliBootstrapSnippet.expires_at).toLocaleTimeString()} and can be used once.</small>
                    </>
                  ) : (
                    <button type="button" className="button" onClick={() => void runAction("cli-bootstrap:invite", () => createCliBootstrapCode("invite_acceptance"))} disabled={isPending("cli-bootstrap:invite")}>
                      {isPending("cli-bootstrap:invite") ? "Creating code..." : "Create CLI setup code"}
                    </button>
                  )}
                  {cliBootstrapMessage && <p className="error-text">{cliBootstrapMessage}</p>}
                </div>
              )}
              {dashboardSection === "members" ? (
                <div className="invite-workspace-panel">
                  <div className="invite-form">
                    <label>
                      Teammate email
                      <input
                        type="email"
                        required
                        value={inviteForm.email}
                        placeholder="teammate@example.com"
                        onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })}
                      />
                    </label>
                    <label>
                      Workspace role
                      <select
                        value={inviteForm.role}
                        onChange={(event) => setInviteForm({ ...inviteForm, role: event.target.value as WorkspaceRole })}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="developer">Developer - regular user</option>
                        <option value="admin">Admin</option>
                        {bootstrap.workspaceRole === "owner" && <option value="owner">Owner</option>}
                      </select>
                    </label>
                    <div className="role-help">
                      <strong>{inviteForm.role}</strong>
                      <p>{roleDescriptions[inviteForm.role]}</p>
                    </div>
                    <button className="button primary" type="button" onClick={() => void runAction("invite:create", createInvite)} disabled={!inviteForm.email || isCreatingInvite || isPending("invite:create")}>
                      {isCreatingInvite || isPending("invite:create") ? "Creating invite..." : "Create invite link"}
                    </button>
                    {inviteError && <p className="error-text">{inviteError}</p>}
                    {inviteMessage && <p className="success-text">{inviteMessage}</p>}
                  </div>
                  {inviteResult && inviteResult.invite_url && (
                    <div className="invite-result">
                      <p className="eyebrow">Invite ready</p>
                      <h3>{inviteResult.email}</h3>
                      <p>Role: <strong>{inviteResult.role}</strong>. This adds workspace membership only; project and channel access are granted separately.</p>
                      <div className="copy-link-row">
                        <input readOnly value={inviteResult.invite_url} />
                        <button className="button" type="button" onClick={() => void runAction("invite:copy", () => copyInviteLink(inviteResult.invite_url ?? ""))} disabled={isPending("invite:copy")}>{isPending("invite:copy") ? "Copying..." : "Copy link"}</button>
                        <button className="button" type="button" onClick={() => void runAction(`invite:send:${inviteResult.id}`, () => sendInviteEmail(inviteResult.id))} disabled={isPending(`invite:send:${inviteResult.id}`)}>{isPending(`invite:send:${inviteResult.id}`) ? "Sending..." : "Send email invite"}</button>
                      </div>
                    </div>
                  )}
                  <div className="pending-invites">
                    <h3>Pending invites</h3>
                    {pendingInvites.length === 0 && <p>No pending invites yet. Create one and send the link in chat.</p>}
                    {pendingInvites.map((invite) => (
                      <article key={invite.id}>
                        <strong>{invite.email}</strong>
                        <span>{invite.role}</span>
                        <small>Expires {new Date(invite.expires_at).toLocaleDateString()}</small>
                        <button type="button" className="secondary-action compact" onClick={() => void runAction(`invite:resend:${invite.id}`, () => resendInvite(invite.id))} disabled={isPending(`invite:resend:${invite.id}`)}>{isPending(`invite:resend:${invite.id}`) ? "Refreshing..." : "Refresh link"}</button>
                        <button type="button" className="secondary-action compact" onClick={() => void runAction(`invite:revoke:${invite.id}`, () => revokeInvite(invite.id))} disabled={isPending(`invite:revoke:${invite.id}`)}>{isPending(`invite:revoke:${invite.id}`) ? "Revoking..." : "Revoke"}</button>
                      </article>
                    ))}
                  </div>
                  <div className="pending-invites">
                    <h3>Workspace members</h3>
                    {dashboardState?.members.map((member) => (
                      <article key={member.user_id}>
                        <strong>{member.email}</strong>
                        <select
                          value={memberRoleDrafts[member.user_id] ?? member.role}
                          onChange={(event) => setMemberRoleDrafts({ ...memberRoleDrafts, [member.user_id]: event.target.value as WorkspaceRole })}
                        >
                          <option value="viewer">viewer</option>
                          <option value="developer">developer</option>
                          <option value="admin">admin</option>
                          {bootstrap.workspaceRole === "owner" && <option value="owner">owner</option>}
                        </select>
                        <small>{member.status}</small>
                        <button type="button" className="secondary-action compact" onClick={() => void runAction(`member:role:${member.user_id}`, () => updateMemberRole(member.user_id, memberRoleDrafts[member.user_id] ?? member.role))} disabled={isPending(`member:role:${member.user_id}`)}>{isPending(`member:role:${member.user_id}`) ? "Saving..." : "Save role"}</button>
                        {member.email !== sessionEmail && member.status === "active" && (
                          <button type="button" className="secondary-action compact" onClick={() => void runAction(`member:deactivate:${member.user_id}`, () => deactivateMember(member.user_id))} disabled={isPending(`member:deactivate:${member.user_id}`)}>{isPending(`member:deactivate:${member.user_id}`) ? "Deactivating..." : "Deactivate"}</button>
                        )}
                        <label>
                          Project role
                          <select
                            value={projectAssignmentDrafts[member.user_id] ?? "developer"}
                            onChange={(event) => setProjectAssignmentDrafts({ ...projectAssignmentDrafts, [member.user_id]: event.target.value as ProjectRole })}
                          >
                            <option value="manager">manager</option>
                            <option value="developer">developer</option>
                            <option value="viewer">viewer</option>
                          </select>
                        </label>
                        <button type="button" className="secondary-action compact" onClick={() => void runAction(`project:assign:${member.user_id}`, () => assignProject(member.user_id, projectAssignmentDrafts[member.user_id] ?? "developer"))} disabled={isPending(`project:assign:${member.user_id}`)}>{isPending(`project:assign:${member.user_id}`) ? "Assigning..." : "Assign project"}</button>
                        <button type="button" className="secondary-action compact" onClick={() => void runAction(`project:remove:${member.user_id}`, () => removeProjectAssignment(member.user_id))} disabled={isPending(`project:remove:${member.user_id}`)}>{isPending(`project:remove:${member.user_id}`) ? "Removing..." : "Remove project"}</button>
                      </article>
                    ))}
                    {adminMessage && <p className={adminMessage.includes("could not") || adminMessage.includes("forbidden") ? "error-text" : "success-text"}>{adminMessage}</p>}
                  </div>
                </div>
              ) : dashboardSection === "keyfiles" || dashboardSection === "keys" ? (
                <div className="invite-workspace-panel">
                  <div className="invite-form">
                    <div className="role-help">
                      <strong>{keyfileBackups.length ? "Encrypted account vault found" : "No encrypted account vault yet"}</strong>
                      <p>Authorized Devices unlock your account vault with passkey PRF. EnvShip stores encrypted vault data and public metadata only.</p>
                    </div>
                    {!accountVaultProductionReady && (
                      <div className="warning-panel">
                        <strong>Add a second Authorized Device before production admin work.</strong>
                        <p>If this is your only device and it is lost, EnvShip support cannot recover your admin unlock power.</p>
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={riskAcknowledgeChecked}
                            onChange={(event) => setRiskAcknowledgeChecked(event.target.checked)}
                          />
                          I understand I could lose access to this project's admin powers if this device is lost.
                        </label>
                        <button
                          type="button"
                          className="secondary-action compact"
                          onClick={() => void runAction("device:risk-ack", acknowledgeOneDeviceRisk)}
                          disabled={!riskAcknowledgeReady || !riskAcknowledgeChecked || isPending("device:risk-ack")}
                        >
                          {isPending("device:risk-ack") ? "Recording..." : riskAcknowledgeReady ? "Record acknowledgement" : "Read warning first"}
                        </button>
                      </div>
                    )}
                    <div className="dashboard-actions">
                      <button type="button" className="button primary" onClick={() => void runAction("vault:add-passkey", addPasskeyUnlockToVault)} disabled={!keyfileBackups.length || !unlockedKeyset || isPending("vault:add-passkey")}>{isPending("vault:add-passkey") ? "Adding device..." : "Add Authorized Device"}</button>
                    </div>
                    {unlockedKeyset && <button type="button" className="button" onClick={clearUnlockedKeyFile}>Clear unlocked vault</button>}
                    <div className="role-help">
                      <strong>{unlockedKeyset ? `Unlocked: ${unlockedKeyset.label}` : "Browser editing is locked"}</strong>
                      <p>EnvShip receives public keys, encrypted key bundles, ciphertext, manifests, and metadata. It does not receive private keys, PRF outputs, plaintext env values, or unwrapped data keys.</p>
                    </div>
                    <label>Channel
                      <select value={editorChannel?.id ?? ""} onChange={(event) => setEditorChannelId(event.target.value)}>
                        {dashboardState?.channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.channel_ref}</option>)}
                      </select>
                    </label>
                    <div className="dashboard-actions">
                      <button type="button" className="button" onClick={() => void runAction("editor:load", loadEditorChannel)} disabled={!unlockedKeyset || !editorChannel || isEditorBusy || isPending("editor:load")}>{isPending("editor:load") ? "Loading..." : "Load current encrypted version"}</button>
                      <button type="button" className="button primary" onClick={() => void runAction("editor:publish", () => publishEditorChannel("edit"))} disabled={!unlockedKeyset || !editorText || isEditorBusy || isPending("editor:publish")}>{isPending("editor:publish") ? "Saving..." : "Save encrypted edit"}</button>
                      <button type="button" className="button" onClick={() => void runAction("editor:rewrap", () => publishEditorChannel("rewrap"))} disabled={!unlockedKeyset || !editorChannel || isEditorBusy || isPending("editor:rewrap")}>{isPending("editor:rewrap") ? "Updating..." : "Update encrypted access"}</button>
                    </div>
                    <label>Local dotenv editor
                      <textarea className="env-editor" value={editorText} onChange={(event) => setEditorText(event.target.value)} placeholder={"API_KEY=...\nDATABASE_URL=..."} spellCheck={false} />
                    </label>
                    {editorExpectedVersionId && <small>Loaded version: {editorExpectedVersionId} / hash {editorBundleHash.slice(0, 12)}</small>}
                    {editorMessage && <p className={/failed|could not|mismatch|changed|required|Unsupported/i.test(editorMessage) ? "error-text" : "success-text"}>{editorMessage}</p>}
                    {activeDeploy && (
                      <div className={`deploy-status-panel ${activeDeploy.status}`}>
                        <div className="role-help">
                          <strong>Deploy status: {deployStatusLabel(activeDeploy.status)}</strong>
                          <p>{activeDeploy.status === "deploy_ready" ? "Encrypted version saved and runtime CDN descriptor is ready." : "Encrypted version saved. Runtime pulls may temporarily see the previous cached descriptor until CDN refresh completes."}</p>
                        </div>
                        <div className="mini-list" aria-label="Deploy checklist">
                          {activeDeploy.checklist.map((item) => (
                            <article key={item.id}>
                              <span>{deployChecklistSymbol(item.status)}</span>
                              <strong>{item.label}: {item.status}</strong>
                              {item.detail && <small>{item.detail}</small>}
                            </article>
                          ))}
                        </div>
                        <div className="dashboard-actions">
                          <button type="button" className="secondary-action compact" onClick={() => void runAction(`deploy:refresh:${activeDeploy.id}`, () => loadDeployDetail(activeDeploy.id))} disabled={isPending(`deploy:refresh:${activeDeploy.id}`)}>{isPending(`deploy:refresh:${activeDeploy.id}`) ? "Refreshing..." : "Refresh deploy status"}</button>
                          {activeDeploy.status !== "deploy_ready" && (
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`deploy:retry:${activeDeploy.id}`, () => retryDeployPurge(activeDeploy.id))} disabled={isPending(`deploy:retry:${activeDeploy.id}`)}>{isPending(`deploy:retry:${activeDeploy.id}`) ? "Retrying..." : "Retry CDN refresh"}</button>
                          )}
                          {deployNeedsNotification(activeDeploy) && (
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`deploy:notify:${activeDeploy.id}`, () => subscribeDeployNotification(activeDeploy.id))} disabled={isPending(`deploy:notify:${activeDeploy.id}`)}>{isPending(`deploy:notify:${activeDeploy.id}`) ? "Enabling..." : "Notify me when deploy is ready"}</button>
                          )}
                        </div>
                        {deployStatusMessage && <p className={/could not|failed|not enabled/i.test(deployStatusMessage) ? "error-text" : "success-text"}>{deployStatusMessage}</p>}
                      </div>
                    )}
                  </div>
                  <div className="section-list">
                    {adminMessage && <p className={adminMessage.includes("could not") ? "error-text" : "success-text"}>{adminMessage}</p>}
                    {authorizedDevices.map((device) => (
                      <article key={device.credential_id_b64u}>
                        <span>-</span>
                        <strong>{device.label ?? "Authorized Device"}: {device.status ?? "active"}{device.last_used_at ? ` - last used ${new Date(device.last_used_at).toLocaleString()}` : ""}</strong>
                        {device.status !== "revoked" && (
                          <div className="dashboard-actions">
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`device:lost:${device.credential_id_b64u}`, () => markAuthorizedDevice(device.credential_id_b64u, "lost"))} disabled={isPending(`device:lost:${device.credential_id_b64u}`)}>{isPending(`device:lost:${device.credential_id_b64u}`) ? "Marking..." : "Mark lost"}</button>
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`device:revoke:${device.credential_id_b64u}`, () => markAuthorizedDevice(device.credential_id_b64u, "revoked"))} disabled={isPending(`device:revoke:${device.credential_id_b64u}`)}>{isPending(`device:revoke:${device.credential_id_b64u}`) ? "Revoking..." : "Revoke"}</button>
                          </div>
                        )}
                      </article>
                    ))}
                    {!authorizedDevices.length && visibleSectionRows.map((item, index) => (
                      <article key={`${item}-${index}`}><span>-</span><strong>{item}</strong></article>
                    ))}
                  </div>
                </div>
              ) : dashboardSection === "channels" ? (
                <div className="invite-workspace-panel">
                  <div className="invite-form">
                    <label>
                      New channel name
                      <input value={channelDraft} placeholder="beta" onChange={(event) => setChannelDraft(event.target.value)} />
                    </label>
                    <button type="button" className="button primary" onClick={() => void runAction("channel:create", createChannel)} disabled={!channelDraft || isPending("channel:create")}>{isPending("channel:create") ? "Creating..." : "Create channel"}</button>
                    <div className="role-help">
                      <strong>Grant exact access</strong>
                      <p>Choose registered public metadata, a channel, and a preset. Project membership alone does not grant decrypt, edit, publish, encrypted access updates, or rollback.</p>
                    </div>
                    <label>Public metadata
                      <select value={grantDraft.keysetId} onChange={(event) => setGrantDraft({ ...grantDraft, keysetId: event.target.value })}>
                        <option value="">Select registered public metadata</option>
                        {dashboardState?.keysets.map((keyset) => <option key={keyset.keyset_id} value={keyset.keyset_id}>{keyset.label} - {keyset.user_email ?? keyset.keyset_id}</option>)}
                      </select>
                    </label>
                    <label>Channel
                      <select value={grantDraft.channelId || selectedChannelRecord?.id || ""} onChange={(event) => setGrantDraft({ ...grantDraft, channelId: event.target.value })}>
                        {dashboardState?.channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.channel_ref}</option>)}
                      </select>
                    </label>
                    <label>Preset
                      <select value={grantDraft.preset} onChange={(event) => setGrantDraft({ ...grantDraft, preset: event.target.value })}>
                        <option value="metadata-only">metadata-only</option>
                        <option value="pull-only">pull-only</option>
                        <option value="editor">editor</option>
                        <option value="publisher">publisher</option>
                        <option value="channel-admin">channel-admin</option>
                      </select>
                    </label>
                    <button type="button" className="button primary" onClick={() => void runAction("channel:grant", grantChannel)} disabled={isPending("channel:grant")}>{isPending("channel:grant") ? "Saving..." : "Save channel grant"}</button>
                    {adminMessage && <p className={adminMessage.includes("could not") ? "error-text" : "success-text"}>{adminMessage}</p>}
                  </div>
                  <div className="section-list">
                    {dashboardState?.channels.map((channel) => (
                      <article key={channel.id}>
                        <span>-</span>
                        <strong>{channel.channel_ref}: version {channel.current_version ?? "none"}</strong>
                      </article>
                    ))}
                    {dashboardState?.grants.map((grant) => (
                      <article key={grant.id}>
                        <span>-</span>
                        <strong>{grant.channel_ref}: {grant.user_email ?? grant.keyset_id} - {grant.grant_status} - {grant.capabilities.join(", ")}</strong>
                        {grant.grant_status !== "revoked" && (
                          <div className="dashboard-actions">
                            <select value={grantPresetDrafts[grant.id] ?? "editor"} onChange={(event) => setGrantPresetDrafts({ ...grantPresetDrafts, [grant.id]: event.target.value })}>
                              <option value="metadata-only">metadata-only</option>
                              <option value="pull-only">pull-only</option>
                              <option value="editor">editor</option>
                              <option value="publisher">publisher</option>
                              <option value="channel-admin">channel-admin</option>
                            </select>
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`grant:update:${grant.id}`, () => updateGrantPreset(grant.id, grantPresetDrafts[grant.id] ?? "editor"))} disabled={isPending(`grant:update:${grant.id}`)}>{isPending(`grant:update:${grant.id}`) ? "Updating..." : "Update preset"}</button>
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`grant:revoke:${grant.id}`, () => revokeGrant(grant.id))} disabled={isPending(`grant:revoke:${grant.id}`)}>{isPending(`grant:revoke:${grant.id}`) ? "Revoking..." : "Revoke grant"}</button>
                          </div>
                        )}
                      </article>
                    ))}
                    {dashboardState?.versions.map((version) => (
                      <article key={version.id}>
                        <span>-</span>
                        <strong>{version.channel_ref}: version {version.version}{version.is_current ? " (current)" : ""}</strong>
                        {!version.is_current && <button type="button" className="secondary-action compact" onClick={() => void runAction(`version:rollback:${version.id}`, () => rollbackVersion(version.channel_ref === selectedChannelRecord?.channel_ref ? selectedChannelRecord.id : (dashboardState.channels.find((channel) => channel.channel_ref === version.channel_ref)?.id ?? ""), version.id))} disabled={isPending(`version:rollback:${version.id}`)}>{isPending(`version:rollback:${version.id}`) ? "Rolling back..." : "Rollback"}</button>}
                      </article>
                    ))}
                    <article>
                      <span>-</span>
                      <strong>Deploy History</strong>
                      <button type="button" className="secondary-action compact" onClick={() => void runAction("deploy:history", loadDeployHistory)} disabled={isPending("deploy:history")}>{isPending("deploy:history") ? "Refreshing..." : "Refresh history"}</button>
                    </article>
                    {deployHistory.map((deploy) => (
                      <article key={deploy.id}>
                        <span>{deployStatusLabel(deploy.status)}</span>
                        <strong>{deploy.channel_ref ?? "channel"}: version {deploy.version ?? deploy.version_id ?? "pending"} - {deploy.operation_type}</strong>
                        <small>{new Date(deploy.created_at).toLocaleString()} - purge attempts {deploy.purge_attempts ?? 0}</small>
                      </article>
                    ))}
                  </div>
                </div>
              ) : dashboardSection === "machines" ? (
                <div className="invite-workspace-panel">
                  <div className="invite-form">
                    <label>
                      Machine name
                      <input
                        value={machineName}
                        placeholder="github-actions-production"
                        onChange={(event) => setMachineName(event.target.value)}
                      />
                    </label>
                    <div className="role-help">
                      <strong>Pull scope</strong>
                      <p>This local setup grants the new deploy identity only to {selectedChannel}. Each online server consumes one machine seat; EnvShip counts conservatively when a fresh instance cannot be matched to an existing active machine.</p>
                    </div>
                    <button type="button" className="button primary" onClick={() => void runAction("machine:create", createMachine)} disabled={isPending("machine:create")}>{isPending("machine:create") ? "Creating..." : "Create machine identity"}</button>
                    <div className="role-help">
                      <strong>Reusable deploy token</strong>
                      <p>Use this for autoscaling or fresh spinups. It is shown once, scoped to {selectedChannel}, stored hashed, and can create new machine seats during the 7-day upgrade grace if usage grows past the plan.</p>
                    </div>
                    <label>
                      Deploy token name
                      <input value={deployTokenName} onChange={(event) => setDeployTokenName(event.target.value)} />
                    </label>
                    <button type="button" className="button" onClick={() => void runAction("machine:deploy-token", createDeployToken)} disabled={isPending("machine:deploy-token")}>{isPending("machine:deploy-token") ? "Creating token..." : "Create reusable deploy token"}</button>
                    {machineMessage && <p className={machineMessage.includes("created") || machineMessage.includes("revoked") || machineMessage.includes("rotated") ? "success-text" : "error-text"}>{machineMessage}</p>}
                    {deployTokenResult && <pre className="code-panel compact">{`ENVSHIP_DEPLOY_TOKEN=${deployTokenResult.token}\npnpm dlx @envshipcom/cli machine install --deploy-token "$ENVSHIP_DEPLOY_TOKEN" --name ${machineName || "envship-machine"}\n# scope: ${deployTokenResult.channels.join(", ")}`}</pre>}
                    {(deployTokens.length ? deployTokens : (dashboardState?.deploy_tokens ?? [])).length ? (
                      <div className="mini-list" aria-label="Reusable deploy tokens">
                        {(deployTokens.length ? deployTokens : (dashboardState?.deploy_tokens ?? [])).map((token) => (
                          <article key={token.id}>
                            <span>{token.status === "active" ? "Active" : "Revoked"}</span>
                            <strong>{token.name}: {token.channels.join(", ") || "no channels"}</strong>
                            <div className="dashboard-actions">
                              <button type="button" className="secondary-action compact" onClick={() => void runAction(`deploy-token:rotate:${token.id}`, () => rotateDeployToken(token.id))} disabled={token.status !== "active" || isPending(`deploy-token:rotate:${token.id}`)}>{isPending(`deploy-token:rotate:${token.id}`) ? "Rotating..." : "Rotate token"}</button>
                              <button type="button" className="secondary-action compact" onClick={() => void runAction(`deploy-token:revoke:${token.id}`, () => revokeDeployToken(token.id))} disabled={token.status !== "active" || isPending(`deploy-token:revoke:${token.id}`)}>{isPending(`deploy-token:revoke:${token.id}`) ? "Revoking..." : "Revoke token"}</button>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : null}
                    {machineInstructions && <pre className="code-panel compact">{machineInstructions}</pre>}
                  </div>
                  <div className="section-list">
                    {(dashboardState?.machines.length ? dashboardState.machines.map((machine) => `${machine.id}|${machine.name}: ${machine.status} - ${machine.channels.join(", ") || "no channels"}`) : sectionRows).map((item, index) => (
                      <article key={`${item}-${index}`}>
                        <span>-</span>
                        <strong>{item}</strong>
                        {dashboardState?.machines[index] && dashboardState.machines[index].status !== "revoked" && (
                          <div className="dashboard-actions">
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`machine:instructions:${dashboardState.machines[index].id}`, () => loadMachineInstructions(dashboardState.machines[index].id))} disabled={isPending(`machine:instructions:${dashboardState.machines[index].id}`)}>{isPending(`machine:instructions:${dashboardState.machines[index].id}`) ? "Loading..." : "Install snippets"}</button>
                            <button type="button" className="secondary-action compact" onClick={() => void runAction(`machine:revoke:${dashboardState.machines[index].id}`, () => revokeMachine(dashboardState.machines[index].id))} disabled={isPending(`machine:revoke:${dashboardState.machines[index].id}`)}>{isPending(`machine:revoke:${dashboardState.machines[index].id}`) ? "Revoking..." : "Revoke"}</button>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                </div>
              ) : dashboardSection === "audit" ? (
                <div className="section-list">
                  <label>Filter audit events<input value={auditFilter} onChange={(event) => setAuditFilter(event.target.value)} placeholder="grant, publish, machine" /></label>
                  <button type="button" className="button primary" onClick={() => void runAction("audit:export", exportAudit)} disabled={isPending("audit:export")}>{isPending("audit:export") ? "Creating..." : "Create audit export"}</button>
                  {adminMessage && <p className={adminMessage.includes("could not") ? "error-text" : "success-text"}>{adminMessage}</p>}
                  {visibleSectionRows.map((item, index) => (
                    <article key={`${item}-${index}`}><span>-</span><strong>{item}</strong></article>
                  ))}
                </div>
              ) : dashboardSection === "billing" ? (
                <div className="section-list">
                  <article className="credential-blocked">
                    <span>-</span>
                    <strong>PayPal subscription checkout is generated when an owner starts an upgrade. Receiver: {paypalReceiverEmail}.</strong>
                  </article>
                  <p className="muted">IPN target: https://api.envship.com/v1/billing/paypal/ipn. Subscription confirmations must validate the receiver before entitlements change.</p>
                  <p className="muted">Browser Dashboard tools are included with account access. CLI Workstations cover trusted coding environments: Free has 1 per user, Pro has 2, Team has 5, and owners can add +5 for one user at $5/mo. Machine seats are billed separately per online server.</p>
                  <button type="button" className="button primary" onClick={() => void runAction("billing:checkout", () => openBillingCheckout({ plan: "pro" }))} disabled={isPending("billing:checkout")}>{isPending("billing:checkout") ? "Opening PayPal..." : "Start Pro subscription"}</button>
                  {adminMessage && <p className={adminMessage.toLowerCase().includes("could not") || adminMessage.toLowerCase().includes("not configured") || adminMessage.toLowerCase().includes("setup-blocked") ? "error-text" : "success-text"}>{adminMessage}</p>}
                  <div className="editor-panel">
                    <h3>CLI Workstations</h3>
                    <p className="muted">Each row is one trusted coding environment connected with an onboarding CLI code. Revoking it removes that local CLI session without affecting browser Dashboard access.</p>
                    {dashboardState?.cli_workstations.length ? dashboardState.cli_workstations.map((workstation) => (
                      <article key={workstation.id}>
                        <span>{workstation.status === "active" ? "active" : "revoked"}</span>
                        <strong>{workstation.label || "CLI Workstation"}</strong>
                        <p>{workstation.user_email ?? workstation.user_id} - {workstation.workstation_id ?? "legacy"} - last used {new Date(workstation.last_used_at).toLocaleString()}</p>
                        {workstation.status === "active" && (
                          <button type="button" className="button danger" onClick={() => void runAction(`cli-workstation:revoke:${workstation.id}`, () => revokeCliWorkstation(workstation.id))} disabled={isPending(`cli-workstation:revoke:${workstation.id}`)}>
                            {isPending(`cli-workstation:revoke:${workstation.id}`) ? "Revoking..." : "Revoke workstation"}
                          </button>
                        )}
                      </article>
                    )) : <p className="muted">No CLI Workstations connected yet.</p>}
                    {dashboardState?.members.map((member) => (
                      <button key={member.user_id} type="button" className="button" onClick={() => void runAction(`billing:addon:${member.user_id}`, () => openBillingCheckout({ addon: "cli_workstations_5", user_id: member.user_id }))} disabled={isPending(`billing:addon:${member.user_id}`)}>
                        {isPending(`billing:addon:${member.user_id}`) ? "Opening PayPal..." : `Add +5 CLI Workstations for ${member.email} ($5/mo)`}
                      </button>
                    ))}
                  </div>
                  {sectionRows.map((item, index) => (
                    <article key={`${item}-${index}`}><span>-</span><strong>{item}</strong></article>
                  ))}
                </div>
              ) : (
                <div className="section-list">
                  {sectionRows.map((item, index) => (
                    <article key={`${item}-${index}`}>
                      <span>-</span>
                      <strong>{item}</strong>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
          {!auth.loading && signedIn && dashboardSection === "overview" && !firstUser && (
            <section className="dashboard-card">
              <p className="eyebrow">Ready</p>
              <h1>{selectedChannel} is ready.</h1>
              <p>The first vault, public metadata, staging grant, and encrypted version are set up. Use the sidebar to manage members, channels, machines, audit, and billing. In local dev, restart clears local Worker data so you can try first-run again.</p>
              <div className="dashboard-actions">
                <button className="button" onClick={editWorkspaceSetup}>Edit workspace details</button>
                <button className="button" onClick={createAnotherWorkspace}>Create another workspace</button>
                <button className="button" onClick={() => void runAction("setup:reset", resetFlow)} disabled={isPending("setup:reset")}>{isPending("setup:reset") ? "Resetting..." : "Restart first-run setup"}</button>
              </div>
            </section>
          )}
        </section>
        <aside className="dashboard-inspector">
          <SentinelPanel />
        </aside>
      </div>
    </main>
  );
}

function InvitePage() {
  const code = window.location.pathname.split("/").pop() ?? "";
  const [auth, setAuth] = React.useState<{ loading: boolean; email: string | null }>({ loading: true, email: null });
  const [invite, setInvite] = React.useState<{
    workspace_name: string;
    email: string;
    role: WorkspaceRole;
    status: string;
    expires_at: string;
  } | null>(null);
  const [status, setStatus] = React.useState("Loading invite...");
  const [error, setError] = React.useState("");
  const { runAction, isPending } = usePendingActions();

  React.useEffect(() => {
    let active = true;
    async function loadInvite() {
      try {
        const [inviteResponse, meResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/v1/invitations/${code}`),
          fetch(`${apiBaseUrl}/v1/me`, { credentials: "include" })
        ]);
        if (!inviteResponse.ok) throw new Error("This invite is invalid, expired, or already used.");
        const inviteBody = await inviteResponse.json() as { invitation: NonNullable<typeof invite> };
        const meBody = meResponse.ok ? await meResponse.json() as { user?: { email: string } } : null;
        if (active) {
          setInvite(inviteBody.invitation);
          setAuth({ loading: false, email: meBody?.user?.email ?? null });
          setStatus("");
        }
      } catch (loadError) {
        if (active) {
          setAuth({ loading: false, email: null });
          setError(loadError instanceof Error ? loadError.message : "Could not load invite.");
          setStatus("");
        }
      }
    }
    void loadInvite();
    return () => {
      active = false;
    };
  }, [code]);

  const acceptInvite = async () => {
    setError("");
    setStatus("Accepting invite...");
    try {
      const response = await fetch(`${apiBaseUrl}/v1/invitations/${code}/accept`, {
        method: "POST",
        credentials: "include"
      });
      const body = await response.json() as { error?: string; retry_after_seconds?: number };
      if (!response.ok) throw new Error(rateLimitMessage(body, "Invite could not be accepted."));
      window.location.href = "/dashboard/members?invite_accepted=1";
    } catch (acceptError) {
      setStatus("");
      setError(acceptError instanceof Error ? acceptError.message : "Invite could not be accepted.");
    }
  };

  const signedInWrongEmail = Boolean(invite && auth.email && auth.email.toLowerCase() !== invite.email.toLowerCase());

  return (
    <main className="login-page invite-page">
      <section className="login-card invite-card">
        <p className="eyebrow">Workspace invite</p>
        <h1>{invite ? `Join ${invite.workspace_name}` : "Open EnvShip invite"}</h1>
        {status && <p>{status}</p>}
        {error && <p className="error-text">{error}</p>}
        {invite && (
          <>
            <div className="invite-detail-grid">
              <div><small>Invited email</small><strong>{invite.email}</strong></div>
              <div><small>Workspace role</small><strong>{invite.role}</strong></div>
              <div><small>Expires</small><strong>{new Date(invite.expires_at).toLocaleString()}</strong></div>
            </div>
            <div className="role-help">
              <strong>{invite.role}</strong>
              <p>{roleDescriptions[invite.role]}</p>
            </div>
            {!auth.loading && !auth.email && (
              <a className="button primary" href={`/login?return_to=${encodeURIComponent(window.location.pathname)}`}>Sign in to accept invite</a>
            )}
            {signedInWrongEmail && (
              <p className="error-text">You are signed in as {auth.email}. This invite is for {invite.email}. Sign out and sign in with the invited email.</p>
            )}
            {!auth.loading && auth.email && !signedInWrongEmail && (
              <button className="button primary" type="button" onClick={() => void runAction("invite:accept", acceptInvite)} disabled={isPending("invite:accept")}>{isPending("invite:accept") ? "Accepting..." : "Accept invite"}</button>
            )}
            <p className="muted-note">Joining a workspace does not grant env access by itself. Project assignment, channel grants, and public vault metadata happen after membership.</p>
          </>
        )}
      </section>
    </main>
  );
}

function AuthorizeCodePage() {
  const initialCode = new URLSearchParams(window.location.search).get("code") ?? "";
  const [auth, setAuth] = React.useState<{ loading: boolean; email: string | null }>({ loading: true, email: null });
  const [dashboardState, setDashboardState] = React.useState<DashboardApiState | null>(null);
  const [userCode, setUserCode] = React.useState(initialCode);
  const [workspaceId, setWorkspaceId] = React.useState("");
  const [channelRefs, setChannelRefs] = React.useState("web/staging");
  const [message, setMessage] = React.useState("");
  const { runAction, isPending } = usePendingActions();
  const [codeStatus, setCodeStatus] = React.useState<{
    status: "waiting" | "pending" | "approved" | "expired" | "already-used" | "wrong-workspace" | "unknown";
    deviceName?: string;
    signingKeyId?: string | null;
    expiresAt?: string;
    approvedChannels?: string[];
  }>({ status: initialCode ? "waiting" : "unknown" });

  React.useEffect(() => {
    let active = true;
    async function load() {
      try {
        const meResponse = await fetch(`${apiBaseUrl}/v1/me`, { credentials: "include" });
        const meBody = meResponse.ok ? await meResponse.json() as { user?: { email: string } } : null;
        const stateResponse = meResponse.ok ? await fetch(`${apiBaseUrl}/v1/dashboard/state`, { credentials: "include" }) : null;
        const stateBody = stateResponse?.ok ? await stateResponse.json() as DashboardApiState : null;
        if (active) {
          setAuth({ loading: false, email: meBody?.user?.email ?? null });
          setDashboardState(stateBody);
          setWorkspaceId(stateBody?.active_workspace_id ?? stateBody?.workspaces[0]?.id ?? "");
          setChannelRefs(stateBody?.channels[0]?.channel_ref ?? "web/staging");
        }
      } catch {
        if (active) setAuth({ loading: false, email: null });
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (!auth.email || !userCode.trim()) return;
    let active = true;
    async function lookup() {
      const response = await fetch(`${apiBaseUrl}/v1/device-codes/lookup?code=${encodeURIComponent(userCode.trim().toUpperCase())}`, {
        credentials: "include"
      });
      const body = await response.json().catch(() => ({})) as {
        status?: string;
        error?: string;
        device_name?: string;
        requested_signing_key_id?: string | null;
        approved_workspace_id?: string | null;
        approved_channel_refs?: string[];
        expires_at?: string;
      };
      if (!active) return;
      if (!response.ok) {
        setCodeStatus({ status: "unknown" });
        return;
      }
      setCodeStatus({
        status: body.status === "approved" ? "approved" : body.status === "expired" ? "expired" : "pending",
        deviceName: body.device_name,
        signingKeyId: body.requested_signing_key_id,
        expiresAt: body.expires_at,
        approvedChannels: body.approved_channel_refs
      });
    }
    const timeout = window.setTimeout(() => void lookup(), 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [auth.email, userCode]);

  const approveCode = async () => {
    setMessage("");
    const refs = channelRefs.split(",").map((item) => item.trim()).filter(Boolean);
    const response = await fetch(`${apiBaseUrl}/v1/device-codes/approve`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_code: userCode.trim().toUpperCase(), workspace_id: workspaceId, channel_refs: refs, device_name: "EnvShip CLI" })
    });
    const body = await response.json().catch(() => ({})) as { error?: string; machine_id?: string; retry_after_seconds?: number };
    if (!response.ok) {
      if (body.error === "device_code_expired") setCodeStatus({ ...codeStatus, status: "expired" });
      if (body.error === "device_code_already_used") setCodeStatus({ ...codeStatus, status: "already-used" });
      setMessage(rateLimitMessage(body, "Approval failed."));
      return;
    }
    setCodeStatus({ ...codeStatus, status: "approved", approvedChannels: refs });
    setMessage(body.machine_id ? `Device approved and machine ${body.machine_id} was scoped to ${refs.join(", ")}.` : "Device approved.");
  };

  const statusLabel = {
    waiting: "Waiting",
    pending: "Waiting",
    approved: "Approved",
    expired: "Expired",
    "already-used": "Already used",
    "wrong-workspace": "Wrong workspace",
    unknown: "Waiting"
  }[codeStatus.status];
  const workspaceName = dashboardState?.workspaces.find((workspace) => workspace.id === workspaceId)?.name ?? "Select workspace";
  const selectedChannels = channelRefs.split(",").map((item) => item.trim()).filter(Boolean);

  return (
    <main className={auth.email ? "dashboard-app authorize-app" : "login-page authorize-login"}>
      {!auth.email ? (
        <section className="login-card authorize-minimal">
          <p className="eyebrow">CLI device approval</p>
          <h1>Sign in to approve this device.</h1>
          <p>The EnvShip CLI created a short-lived approval code. Sign in, choose the workspace and channel scope, then the CLI stores its local device credential for future commands.</p>
          <a className="button primary" href={`/login?return_to=${encodeURIComponent(`/authorize-code?code=${userCode}`)}`}>Sign in to approve device</a>
        </section>
      ) : (
        <>
          <header className="dashboard-topbar">
            <a className="brand" href="/"><span className="brand-mark" />EnvShip</a>
            <div className="topbar-selectors">
              <div><small>Approval</small><strong>CLI device approval code</strong></div>
              <div><small>Workspace</small><strong>{workspaceName}</strong></div>
              <div><small>User</small><strong>{auth.email}</strong></div>
            </div>
            <span className={`grant-chip ${codeStatus.status === "approved" ? "good" : codeStatus.status === "expired" || codeStatus.status === "already-used" ? "warn" : ""}`}>{statusLabel}</span>
            <a className="secondary-action compact" href="/dashboard">Dashboard</a>
          </header>
          <div className="dashboard-layout authorize-layout">
            <aside className="dashboard-sidebar">
              <nav className="dashboard-nav">
                <a className="active" href="/authorize-code">Approve device</a>
                <a href="/dashboard/machines">Machines</a>
                <a href="/dashboard/channels">Channel grants</a>
                <a href="/dashboard/audit">Audit</a>
              </nav>
            </aside>
            <section className="dashboard-main">
              <section className="dashboard-card authorize-panel">
                <div className="dashboard-card-header">
                  <div>
                    <p className="eyebrow">Device approval</p>
                    <h1>Approve a scoped CLI or machine device.</h1>
                    <p>Codes expire after one hour if unused. Approval is a setup window only; approved devices keep a local credential and do not need a new code for every command.</p>
                  </div>
                  <span className={`approval-status ${codeStatus.status}`}>{statusLabel}</span>
                </div>
                <div className="approval-grid">
                  <label>Approval code<input value={userCode} onChange={(event) => setUserCode(event.target.value)} placeholder="Paste CLI code" /></label>
                  <label>Workspace
                    <select value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
                      {dashboardState?.workspaces.map((workspace) => <option value={workspace.id} key={workspace.id}>{workspace.name}</option>)}
                    </select>
                  </label>
                  <label>Allowed channels<input value={channelRefs} onChange={(event) => setChannelRefs(event.target.value)} placeholder="web/staging, web/production" /></label>
                </div>
                <div className="approval-summary">
                  <div><small>Device name</small><strong>{codeStatus.deviceName ?? "EnvShip CLI"}</strong></div>
                  <div><small>Requested signing key</small><strong>{codeStatus.signingKeyId ?? "Shown after code lookup"}</strong></div>
                  <div><small>Expires</small><strong>{codeStatus.expiresAt ? new Date(codeStatus.expiresAt).toLocaleString() : "One hour after CLI request"}</strong></div>
                  <div><small>Scope</small><strong>{selectedChannels.join(", ") || "No channels selected"}</strong></div>
                </div>
                <button className="button primary" type="button" onClick={() => void runAction("device:approve", approveCode)} disabled={!userCode || !workspaceId || codeStatus.status === "approved" || codeStatus.status === "expired" || isPending("device:approve")}>{isPending("device:approve") ? "Approving..." : "Approve device"}</button>
                {message && <p className={message.includes("approved") ? "success-text" : "error-text"}>{message}</p>}
              </section>
            </section>
            <aside className="dashboard-inspector">
              <section className="sentinel-panel approval-context">
                <p className="eyebrow">Security context</p>
                <h3>What approval does</h3>
                <ul>
                  <li>Stores a local CLI/device credential after approval.</li>
                  <li>Scopes the device to selected workspace channels.</li>
                  <li>Never gives the CLI private vault material or plaintext env values.</li>
                  <li>Writes an audit event for approval and machine creation.</li>
                </ul>
              </section>
            </aside>
          </div>
        </>
      )}
    </main>
  );
}

function UnlockPage() {
  const params = new URLSearchParams(window.location.search);
  const returnTo = sanitizeReturnTo(params.get("return_to"));
  const deviceRequestCode = params.get("device_request");
  const [auth, setAuth] = React.useState<{ loading: boolean; email: string | null; error: string | null }>({
    loading: true,
    email: null,
    error: null
  });
  const [accountVault, setAccountVault] = React.useState<{
    vault: { encrypted_key_bundle: EncryptedKeyBundle } | null;
    devices: WebauthnCredentialSummary[];
    device_count: number;
    production_ready: boolean;
  } | null>(null);
  const [deviceRequestSent, setDeviceRequestSent] = React.useState(false);
  const [pendingRequestCode, setPendingRequestCode] = React.useState(deviceRequestCode ?? "");
  const [unlockMessage, setUnlockMessage] = React.useState("");
  const pendingDevicePrivateKey = React.useRef<CryptoKey | null>(null);
  const { runAction, isPending } = usePendingActions();

  React.useEffect(() => {
    let active = true;
    async function loadSession() {
      try {
        const response = await fetch(`${apiBaseUrl}/v1/me`, { credentials: "include" });
        if (!response.ok) {
          window.location.href = loginUrlFor(returnTo);
          return;
        }
        const body = await response.json() as { user?: { email?: string } };
        const vaultResponse = await fetch(`${apiBaseUrl}/v1/account-vault`, { credentials: "include" });
        const vaultBody = await vaultResponse.json().catch(() => null) as typeof accountVault | null;
        if (active) {
          setAuth({ loading: false, email: body.user?.email ?? null, error: null });
          setAccountVault(vaultResponse.ok ? vaultBody : null);
        }
      } catch {
        if (active) setAuth({ loading: false, email: null, error: "Could not reach the EnvShip API." });
      }
    }
    void loadSession();
    return () => {
      active = false;
    };
  }, [returnTo]);

  const createFirstAuthorizedDevice = async () => {
    if (!auth.email) return;
    setUnlockMessage("");
    const passkey = await registerPasskeyForVault(apiBaseUrl, "First Authorized Device");
    if (!passkey.prfOutput) throw new Error("This passkey did not return PRF unlock material. EnvShip requires passkey PRF support.");
    const keyset = await generatePlainKeyset("account-vault");
    const encryptedKeyBundle = await createPrfOnlyEncryptedKeyBundle({ keyset, credentialIdB64u: passkey.credentialIdB64u, prfOutput: passkey.prfOutput });
    const response = await fetch(`${apiBaseUrl}/v1/account-vault`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ encrypted_key_bundle: encryptedKeyBundle })
    });
    if (!response.ok) throw new Error("Encrypted account vault could not be saved.");
    setAccountVaultSession({ email: auth.email, keyset, encryptedKeyBundle, credentialIdB64u: passkey.credentialIdB64u });
    writeUnlockSession(window.sessionStorage, auth.email);
    setUnlockMessage("Authorized Device created. Going to Dashboard...");
    window.setTimeout(() => {
      navigateWithinApp(returnTo);
    }, 250);
  };

  const unlockWithPasskey = async () => {
    if (!auth.email || !accountVault?.vault?.encrypted_key_bundle) return;
    setUnlockMessage("");
    const passkey = await authenticatePasskeyForVault(apiBaseUrl);
    const keyset = await unlockKeyBundleWithPrf(accountVault.vault.encrypted_key_bundle, passkey.credentialIdB64u, passkey.prfOutput);
    setAccountVaultSession({ email: auth.email, keyset, encryptedKeyBundle: accountVault.vault.encrypted_key_bundle, credentialIdB64u: passkey.credentialIdB64u });
    writeUnlockSession(window.sessionStorage, auth.email);
    if (deviceRequestCode) {
      setUnlockMessage("Authorized Device unlock complete. Review the pending device request before approval.");
      return;
    }
    setUnlockMessage("Authorized Device unlock complete. Going to Dashboard...");
    window.setTimeout(() => {
      navigateWithinApp(returnTo);
    }, 250);
  };

  const requestNewDevice = async () => {
    setDeviceRequestSent(true);
    const pair = await crypto.subtle.generateKey({ name: "RSA-OAEP", modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" }, true, ["encrypt", "decrypt"]);
    pendingDevicePrivateKey.current = (pair as CryptoKeyPair).privateKey;
    const publicJwk = await crypto.subtle.exportKey("jwk", (pair as CryptoKeyPair).publicKey);
    const response = await fetch(`${apiBaseUrl}/v1/authorized-device-requests`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ephemeral_public_jwk: publicJwk })
    });
    const body = await response.json().catch(() => ({})) as { approve_url?: string; error?: string };
    if (!response.ok) throw new Error(body.error ?? "Device authorization request could not be created.");
    const code = body.approve_url ? new URL(body.approve_url).searchParams.get("device_request") : "";
    if (code) setPendingRequestCode(code);
    setUnlockMessage(`An authorization request has been sent to your account. Sign in with an Authorized Device to approve this new device. Approval link: ${body.approve_url ?? "pending"}`);
  };

  const approveDeviceRequest = async () => {
    try {
      if (!auth.email || !deviceRequestCode) return;
      const session = getAccountVaultSession(auth.email);
      if (!session) throw new Error("Unlock this Authorized Device before approving a new one.");
      const lookup = await fetch(`${apiBaseUrl}/v1/authorized-device-requests/${encodeURIComponent(deviceRequestCode)}`, { credentials: "include" });
      const lookupBody = await lookup.json().catch(() => ({})) as { request?: { ephemeral_public_jwk?: JsonWebKey }; error?: string };
      if (!lookup.ok || !lookupBody.request?.ephemeral_public_jwk) throw new Error(lookupBody.error ?? "Device request could not be loaded.");
      const encryptedTransfer = await encryptKeysetForDeviceTransfer(session.keyset, lookupBody.request.ephemeral_public_jwk);
      const response = await fetch(`${apiBaseUrl}/v1/authorized-device-requests/${encodeURIComponent(deviceRequestCode)}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encrypted_transfer: encryptedTransfer, approving_credential_id_b64u: session.credentialIdB64u })
      });
      const body = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(body.error ?? "Device request could not be approved.");
      setUnlockMessage("Authorized Device request approved. The new browser can now finish passkey setup.");
    } catch (error) {
      setUnlockMessage(error instanceof Error ? error.message : "Device request could not be approved.");
    }
  };

  const finishApprovedDeviceRequest = async () => {
    if (!auth.email || !pendingRequestCode || !pendingDevicePrivateKey.current || !accountVault?.vault?.encrypted_key_bundle) return;
    const response = await fetch(`${apiBaseUrl}/v1/authorized-device-requests/${encodeURIComponent(pendingRequestCode)}/consume`, {
      method: "POST",
      credentials: "include"
    });
    const body = await response.json().catch(() => ({})) as { encrypted_transfer?: { wrapped_key_b64u?: string; iv_b64u?: string; ciphertext_b64u?: string }; error?: string };
    if (!response.ok || !body.encrypted_transfer) throw new Error(body.error ?? "Approved device transfer is not ready yet.");
    const keyset = await decryptKeysetFromDeviceTransfer(body.encrypted_transfer, pendingDevicePrivateKey.current);
    const passkey = await registerPasskeyForVault(apiBaseUrl, "Authorized Device");
    if (!passkey.prfOutput) throw new Error("This passkey did not return PRF unlock material. EnvShip requires passkey PRF support.");
    const encryptedKeyBundle = await upsertPrfWrapForKeyBundle({
      bundle: accountVault.vault.encrypted_key_bundle,
      keyset,
      credentialIdB64u: passkey.credentialIdB64u,
      prfOutput: passkey.prfOutput
    });
    const save = await fetch(`${apiBaseUrl}/v1/account-vault`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ encrypted_key_bundle: encryptedKeyBundle })
    });
    if (!save.ok) throw new Error("Authorized Device vault wrap could not be saved.");
    pendingDevicePrivateKey.current = null;
    setAccountVaultSession({ email: auth.email, keyset, encryptedKeyBundle, credentialIdB64u: passkey.credentialIdB64u });
    writeUnlockSession(window.sessionStorage, auth.email);
    setUnlockMessage("Authorized Device added. Going to Dashboard...");
    window.setTimeout(() => navigateWithinApp(returnTo), 250);
  };

  return (
    <main className="login-page unlock-page">
      <section className="login-card unlock-card">
        <p className="eyebrow">Account verified</p>
        <h1>Unlock EnvShip</h1>
        <p>OAuth confirms who you are. An Authorized Device unlocks browser work before Dashboard opens.</p>
        <div className="unlock-steps">
          <div className="unlock-step complete"><span>1</span><strong>OAuth Login Successful</strong><small>{auth.email ?? "Checking session..."}</small></div>
          <div className={`unlock-step ${isPending("unlock:create") || isPending("unlock:passkey") ? "active" : ""}`}><span>2</span><strong>Unlocking Authorized Device</strong><small>Passkey PRF unlock required</small></div>
          <div className={`unlock-step ${unlockMessage.includes("complete") ? "complete" : ""}`}><span>3</span><strong>Dashboard Ready</strong><small>Continue after device unlock succeeds.</small></div>
        </div>
        {auth.loading && <p>Loading Passkey...</p>}
        {auth.error && <p className="error-text">{auth.error}</p>}
        {!auth.loading && auth.email && !accountVault?.vault && (
          <button type="button" className="button primary" onClick={() => void runAction("unlock:create", createFirstAuthorizedDevice)} disabled={isPending("unlock:create")}>
            {isPending("unlock:create") ? "Creating Authorized Device..." : "Create your first Authorized Device"}
          </button>
        )}
        {!auth.loading && auth.email && accountVault?.vault && (
          <button type="button" className="button primary" onClick={() => void runAction("unlock:passkey", unlockWithPasskey)} disabled={isPending("unlock:passkey")}>
            {isPending("unlock:passkey") ? "Unlocking..." : "Unlock with passkey"}
          </button>
        )}
        {deviceRequestCode && getAccountVaultSession(auth.email) && (
          <button type="button" className="button primary" onClick={() => void approveDeviceRequest()} disabled={isPending("unlock:approve-device")}>
            {isPending("unlock:approve-device") ? "Approving..." : "Approve new Authorized Device"}
          </button>
        )}
        <button type="button" className="link-button" onClick={() => void runAction("unlock:new-device", requestNewDevice)}>Click here if this is a new device.</button>
        {pendingRequestCode && pendingDevicePrivateKey.current && (
          <button type="button" className="button" onClick={() => void runAction("unlock:finish-device", finishApprovedDeviceRequest)} disabled={isPending("unlock:finish-device")}>
            {isPending("unlock:finish-device") ? "Finishing..." : "Finish after approval"}
          </button>
        )}
        {deviceRequestSent && <div className="callout compact">{unlockMessage}</div>}
        {!deviceRequestSent && unlockMessage && <p className="success-text">{unlockMessage}</p>}
      </section>
    </main>
  );
}

function LoginPage() {
  const returnTo = new URLSearchParams(window.location.search).get("return_to");
  const safeReturnTo = sanitizeReturnTo(returnTo);
  const unlockReturnTo = unlockUrlFor(safeReturnTo);
  const [loginError, setLoginError] = React.useState("");
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const { runAction, isPending } = usePendingActions();
  const oauthHref = (provider: string) => `${apiBaseUrl}/v1/auth/oauth/${provider}/start?return_to=${encodeURIComponent(unlockReturnTo)}`;
  const useTestLogin = async () => {
    setIsLoggingIn(true);
    setLoginError("");
    try {
      const response = await fetch(`${apiBaseUrl}/v1/auth/local-test-login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: testLoginEmail, password: testLoginPassword })
      });
      const body = await response.json().catch(() => ({})) as { redirect_url?: string; error?: string; retry_after_seconds?: number };
      if (!response.ok) throw new Error(rateLimitMessage(body, "Local test login failed."));
      window.location.href = unlockReturnTo;
    } catch {
      setLoginError(isStagingApp()
        ? "Staging test login is not enabled for this API deployment."
        : "Local test login could not reach api.envship.test. Start the Worker dev server and try again.");
      setIsLoggingIn(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-heading">
          <p className="eyebrow">Secure login</p>
          <h1>Sign in to EnvShip</h1>
          <p>Use OAuth to verify your account, then unlock Dashboard work with an Authorized Device.</p>
        </div>
        <div className="social-login-stack">
          <a className="social-login-button" href={oauthHref("google")}><span>G</span>Continue with Google</a>
          <a className="social-login-button" href={oauthHref("github")}><span>GH</span>Continue with GitHub</a>
          <a className="social-login-button" href={oauthHref("atlassian")}><span>J</span>Continue with Jira</a>
        </div>
        {(isLocalDevApp() || isStagingApp()) && <div className="test-login">
          <strong>{isStagingApp() ? "Staging test login" : "Local test login"}</strong>
          <p>{isStagingApp() ? "Staging-only QA path. This must be disabled on final production." : "This creates a local API session, then opens the unlock screen."}</p>
          {loginError && <p className="error-text">{loginError}</p>}
          <button type="button" onClick={() => void runAction("login:local", useTestLogin)} disabled={isLoggingIn || isPending("login:local")}>{isLoggingIn || isPending("login:local") ? "Signing in..." : `Test login as ${testLoginEmail}`}</button>
        </div>}
        <p className="login-footnote">OAuth proves identity. EnvShip cannot decrypt your secure data without a browser-side Authorized Device unlock.</p>
      </section>
    </main>
  );
}

function PageHero(props: { label: string; title: string; text: string }) {
  return (
    <section className="page-hero">
      <p className="eyebrow">{props.label}</p>
      <h1>{props.title}</h1>
      <p>{props.text}</p>
    </section>
  );
}

function SectionIntro(props: { label: string; title: string; text: string }) {
  return (
    <div className="section-intro">
      <p className="eyebrow">{props.label}</p>
      <h2>{props.title}</h2>
      <p>{props.text}</p>
    </div>
  );
}

function CommandPanel(props: { title: string; lines: string[] }) {
  return (
    <aside className="command-panel">
      <p>{props.title}</p>
      {props.lines.map((line) => <code key={line}>{line}</code>)}
    </aside>
  );
}

const rootElement = document.getElementById("root")!;
const rootStore = globalThis as typeof globalThis & { __envshipRoot?: ReturnType<typeof createRoot> };
rootStore.__envshipRoot ??= createRoot(rootElement);
rootStore.__envshipRoot.render(<App />);
